import { getBooking } from "../../../app/api/getBooking";
import { getStops } from "../../../app/api/getStops";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { calculatePrice, convertISO8601ToDuration, convertTimeToArabic, getShortDateFromDateStringFormatted, getTrainClassTypeBaseOnProduct, isActionRunSuccessfully, toUTC4 } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedOffer, IBookedTrips, IBookingPassenger, IBookingSearchResult, IBookingTypeTrip, IPassenger, ISelectedPassenger, StationsListType } from "../../../Orchestrator/Types/Index";
import { IGetAllTripWithoutExchangeActionParams, IGetPassengerListByOfferAdmissionsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { IBookingResponse } from "../../../Orchestrator/Types/Types/BookingTypes";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { getPassengerListByOfferAdmissionsAction } from "../CommonActions/GetPassengerListByOfferAdmissionsAction";
import stopManager from "../../../Lib/Utils/StopManager";
import { preferTrainTrips, tripsStillHeld } from "../../../Lib/Utils/booking";
import { shuttleServesDirection } from "../../../Lib/Utils/tripDirectionRecovery";
import { eligibleShuttleTargets, shuttleSideClosesAt, trainLegsFromBooking } from "../../../Components/EndToEndJourney/shuttleAddRequest";
import { getExchangeOfferByBooking } from "../SearchBooking/GetExchangeOfferByBooking";
import { appStore } from "../../../Orchestrator/Recoils/store";
import { shuttleStopAvailabilityAtom } from "../../../Orchestrator/Recoils/EndToEndJourneyAtom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProductTypeEnum, trainType, TripTypeEnum, isShuttleOffer, isShuttleProductCode } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import { transformStops } from "../Search/GetStopsAction";
import { computeNoModificationOnBooking } from "./bookingEligibility";
import { collapseBookingRows } from "./collapseBookingRows";
import { splitRowTripsByLeg } from "./splitRowTripsByLeg";
import {
    cdtGroupingIds,
    groupingPassengerIdsForTrip,
    legHolderIds,
    movedPassengerIdsOf,
    nonExchangedPassengerIds,
    wasMovedOffGroup,
} from "./bookingRowRiders";
import { autoCancelOngoingExchangesAction, IAutoCancelOngoingExchangePair } from "./AutoCancelOngoingExchangesAction";
import { bookingsPastPivot, bookingsPastPivotDubai, isDepartureInTab } from "../../../Lib/Utils/bookingAppearancePeriod";
import { dedupeBookingSummaries } from "./bookingDisplayPagination";
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * A booking passenger as a list card carries it: name and email defaulted to ""
 * so the card never renders "undefined". One copy — it was written out inline at
 * every card builder, and those copies are what drift.
 */
const toCardPassenger = (passenger: IPassenger): IPassenger => ({
    ...passenger,
    detail: {
        ...passenger?.detail || {},
        firstName: passenger?.detail?.firstName || "",
        lastName: passenger?.detail?.lastName || "",
        email: passenger?.detail?.email || "",
    },
} as IPassenger);

function getProductNameFromOffer(offer: any): string {
    const product = offer?.products?.find((p: any) => p.type === ProductTypeEnum.ADMISSION_POINT2POINT) || offer?.products?.[0];
    // Verbatim catalog name ("Comfort Flex") — no class-prefix stripping on
    // booking cards; stripping is for search fare cards under class columns.
    return product?.summary || product?.description || "";
}

/**
 * Stamps each list row with "until when can this booking still take a shuttle",
 * using the SAME per-side rule the booking-details menu and the End-to-end
 * Journey section use. The row shape carries no legs or offers, so it has to be
 * decided here, where the full booking is in hand — otherwise the list falls
 * back to a per-trip guess and disagrees with the page it links to.
 *
 * An instant rather than a yes/no: the latest closing time across the eligible
 * targets (shuttleSideClosesAt), so a list left open past a departure or the end
 * of the appearance period drops the item on time instead of keeping it until
 * the next rebuild. Only the CLOCK is projected forward — a shuttle bought
 * elsewhere meanwhile still needs a refresh, as every other stamped field does.
 * null when nothing is eligible now.
 *
 * Undefined when the booking was not fetched this run — and GetBookItAgainTripList
 * publishes rows without passing through here at all — so the card falls back to a
 * station check rather than silently hiding an action.
 */
export function stampShuttleEligibility(
    rows: IBookedTrips[],
    bookings: Map<string, IBookingResponse>,
    allStops: StationsListType,
    isShuttleEnabled: boolean,
): IBookedTrips[] {
    return (rows || []).map((row) => {
        const booking = bookings.get(row?.bookingId)?.booking;
        if (!booking) return row;
        const legs = trainLegsFromBooking(booking);
        const targets = eligibleShuttleTargets({
            booking,
            legs,
            allStops: allStops || [],
            shuttleAdded: {},
            refundedOfferIds: [],
            isShuttleEnabled,
            // A partially-refunded booking still stamps FULFILLED (see the
            // status derivation below), so it keeps the action. Past rows
            // render a different menu array entirely.
            isActiveBooking: row?.status === "FULFILLED",
            // The section's own stop sweep, when it has run this session: a side
            // whose every stop is known-empty is not offered there, so not here.
            // Unswept = unknown = kept.
            stopAvailability: appStore.get(shuttleStopAvailabilityAtom),
        });
        return {
            ...row,
            canAddShuttleUntil: targets.length > 0
                ? Math.max(...targets.map((target) =>
                    shuttleSideClosesAt(legs.find((leg) => leg.legId === target.legId), target.side)))
                : null,
        };
    });
}

interface IGetBookingToRightDataActionParams {
    bookedTrips: IBookingSearchResult[];
    activeTab?: string;
    isAnonymous?: boolean;
    /**
     * Append-mode for lazy "Load more" pagination (both tabs). When true:
     *  - Newly-built rows are concatenated onto the current bookedTripsAtom
     *    instead of overwriting it (the page-only publish above is skipped).
     *  - The next-ticket / nearestRecord / exchange-offers prefetch logic is
     *    skipped — the next ticket is picked once, on the first page, and a
     *    later page must not re-pick or shuffle it.
     */
    append?: boolean;
}

class GetBookingToRightDataAction extends BaseAction<void, IGetBookingToRightDataActionParams> {
    override async run(): Promise<void> {
        try {
            // Folded before anything is fetched or built: the bookings search
            // answers one row PER ADMISSION, so an unfolded page would GET the
            // same booking once per passenger and then render it once per
            // passenger. Callers that already folded pass through untouched.
            const bookedTrips: IBookingSearchResult[] = dedupeBookingSummaries(this.params?.bookedTrips || []);
            const isAnonymous: boolean = this.params?.isAnonymous || false
            // Per-run cache of the full booking response from the outer
            // Promise.all loop. The "nearestRecord" post-loop block (and the
            // quick-exchange sub-action it invokes) used to GET the same
            // booking 3 more times for the upcoming next-ticket — every one
            // reading data already in this map.
            const bookingResponseCache = new Map<string, IBookingResponse>();
            const exchangeOngoingPairs: IAutoCancelOngoingExchangePair[] = [];
            const getCachedOrFetchBooking = async (bookingId: string): Promise<{ status: string; answer?: IBookingResponse }> => {
                const cached = bookingResponseCache.get(bookingId);
                if (cached) return { status: 'ok', answer: cached };
                return getBooking(bookingId, false);
            };
            if (bookedTrips.length) {
                // Get stops data for station names
                const stopResponse: any = await getStops();
                const stops = stopResponse.status === "ok" ? stopResponse.answer.stops : [];
                // Kept in scope: the rows published below carry a shuttle-eligibility
                // flag, and deciding it needs the transformed stations (which hold the
                // shuttle sub-stations) plus the feature flag.
                let stationList: StationsListType = [];
                let isShuttleEnabled = false;
                if (stops.length > 0) {
                    const config = await getRuntimeConfig();
                    stationList = transformStops(stops, config.NEXT_PUBLIC_STOP_EXTERNAL_CODE)
                    isShuttleEnabled = config.NEXT_PUBLIC_ENABLE_SHUTTLE_BUS === "true";
                    updateRecoilState<StationsListType>("stopsResponseAtom", stationList);
                }
                const withShuttleFlag = (rows: IBookedTrips[]) =>
                    stampShuttleEligibility(rows, bookingResponseCache, stationList, isShuttleEnabled);
                const updateBookedTrips: IBookedTrips[] = await Promise.all(bookedTrips.map(async (bookingTrip: any) => {
                    const response: any = await getBooking(bookingTrip.id, false)
                    if (response.status === 'ok' && response.answer) {
                        const bookingResponse: IBookingResponse = response.answer;
                        bookingResponseCache.set(bookingTrip.id, bookingResponse);
                        const ongoingAdmissions = (bookingResponse.booking?.bookedOffers || []).flatMap(
                            (offer: any) => (offer.admissions || []).filter((a: any) => a.status === "EXCHANGE_ONGOING")
                        );
                        if (ongoingAdmissions.length > 0) {
                            const openOp = ((bookingResponse.booking?.exchangeOperations || []) as any[]).find(
                                (op: any) => op?.status === "PREBOOKED" || op?.status === "CONFIRMED"
                            );
                            if (openOp?.id) {
                                exchangeOngoingPairs.push({
                                    bookingId: bookingTrip.id,
                                    exchangeOperationId: openOp.id,
                                });
                            }
                        }

                        const allBookedOffers = bookingResponse.booking?.bookedOffers || [];
                        /**
                         * Does this offer TOUCH a shuttle? A different question from
                         * `isShuttleOffer` ("is this offer the shuttle rather than the train"),
                         * and it needs the opposite treatment of ancillaries.
                         *
                         * A shuttle sold as an ANCILLARY_ITEM alongside a train — booking
                         * 5HQ2PP's "Shuttle From/To Reem Mall" — still means the passenger
                         * rides a shuttle, so the booking-level flags below must count it.
                         * `isShuttleOffer` deliberately does not: for "which offer names the
                         * card" that same ancillary is what made a train read as a shuttle.
                         *
                         * Same products, two questions, two answers. Do not merge them.
                         *
                         * Code only, deliberately: the predicate this replaced also matched on
                         * `summary` text. Every shuttle product observed on the wire is
                         * SHTL-prefixed ("SHTL", "SHTL-MBZ-REEM", "SHTL_ADNEC-MBZ"), so the
                         * code test already covers them, and matching free text is what let
                         * "Shuttle From/To Reem Mall" speak for the train offer it rode on.
                         */
                        const offerTouchesShuttle = (offer: any): boolean =>
                            (offer?.products || []).some((product: any) => isShuttleProductCode(product?.code));
                        const isBookingHasShuttle = allBookedOffers.some((offer: any) =>
                            offer?.admissions?.some((admission: any) => admission.status !== "EXCHANGED" && admission.status !== "ON_HOLD") &&
                            offerTouchesShuttle(offer)
                        );
                        // Shuttle-ONLY booking (standalone Shuttle flow): every offer is a
                        // shuttle. A train booking with an onward shuttle is NOT shuttle-only
                        // and must keep the full manage-booking menu.
                        const isShuttleOnlyBooking = allBookedOffers.length > 0 && allBookedOffers.every(isShuttleOffer);
                        const { booking } = bookingResponse

                        const hasFulfilledAdmission = booking.bookedOffers?.some((offer: any) =>
                            offer.admissions?.some((admission: any) => admission.status === "FULFILLED" || admission.status === "REFUNDED" || admission.status === "RELEASED" || admission.status === "ON_HOLD" || admission.status === "EXCHANGE_ONGOING")
                        );

                        if (!hasFulfilledAdmission) {
                            return [];
                        }

                        let allBookedOffersWithoutExchange: IBookedOffer[] = []
                        let allTripWithoutExchange: IBookingTypeTrip[] = []

                        // Process booked offers and determine status
                        const bookedOffers = booking.bookedOffers || [];

                        await getAllOfferWithoutExchangeAction.setParams(bookedOffers);
                        await getAllOfferWithoutExchangeAction.execute();
                        if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
                            allBookedOffersWithoutExchange =
                                getAllOfferWithoutExchangeAction?.result || [];

                            const params: IGetAllTripWithoutExchangeActionParams = {
                                bookedOffers: allBookedOffersWithoutExchange,
                                trips: booking.trips,
                            };
                            await getAllTripWithoutExchangeAction.setParams(params);
                            await getAllTripWithoutExchangeAction.execute();
                            if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
                                allTripWithoutExchange =
                                    getAllTripWithoutExchangeAction?.result || [];
                            }
                        }

                        // Per-direction exchange-slot eligibility. Backend rule:
                        // one exchange per booking, with round-trip allowed an
                        // exchange per direction (outbound + inbound are
                        // independent slots). For each direction, a slot is
                        // available only if (a) the booking actually has
                        // offers on that direction and (b) none of those
                        // offers carry an EXCHANGED admission. Combined
                        // (non-split) booking entries below get the OR of the
                        // two directions; split entries use their own.
                        const bookingTrips = booking.trips || [];
                        const isOnDirection = (offer: any, direction: string) => {
                            const tripId = offer?.tripCoverage?.coveredTripId;
                            if (!tripId) return false;
                            const trip = bookingTrips.find((t: any) => t?.id === tripId);
                            return trip?.direction === direction;
                        };
                        const outboundOffers = bookedOffers.filter((o: any) => isOnDirection(o, TripTypeEnum.OUT_BOUND));
                        const inboundOffers = bookedOffers.filter((o: any) => isOnDirection(o, TripTypeEnum.IN_BOUND));
                        // Orphan offers: belong to the booking but their trip
                        // has no direction (or trip id doesn't match any trip on
                        // the booking — partial sync, shuttle-only leg, data
                        // corruption). If any such offer carries an EXCHANGED
                        // admission, the booking-wide slot is consumed even
                        // though we can't classify it to a direction. Fold the
                        // orphan check into both per-direction flags so the
                        // orphan can never fall through silently.
                        const orphanOffers = bookedOffers.filter(
                            (o: any) => !isOnDirection(o, TripTypeEnum.OUT_BOUND) && !isOnDirection(o, TripTypeEnum.IN_BOUND),
                        );
                        const orphanNoMod = orphanOffers.length === 0 || computeNoModificationOnBooking(orphanOffers);

                        // Fallback: when offers don't map to a known direction
                        // (legacy data, shuttle-only bookings, trip id mismatch,
                        // etc.) fall back to the legacy booking-wide check so
                        // we don't accidentally hide every chip for offers we
                        // can't classify.
                        const hasDirectionalOffers = outboundOffers.length > 0 || inboundOffers.length > 0;
                        const bookingLevelNoMod = computeNoModificationOnBooking(bookedOffers);
                        const noModOutbound = hasDirectionalOffers
                            ? outboundOffers.length > 0 && computeNoModificationOnBooking(outboundOffers) && orphanNoMod
                            : bookingLevelNoMod;
                        const noModInbound = hasDirectionalOffers
                            ? inboundOffers.length > 0 && computeNoModificationOnBooking(inboundOffers) && orphanNoMod
                            : bookingLevelNoMod;
                        const noModificationOnBooking = hasDirectionalOffers
                            ? (noModOutbound || noModInbound)
                            : bookingLevelNoMod;

                        // Fallback logic for calculating prices and determining status if no structured trips were created
                        let outboundConfirmedPrice = 0;
                        let inboundConfirmedPrice = 0;

                        allBookedOffersWithoutExchange.forEach((offer: any) => {
                            offer?.admissions?.forEach((admission: any) => {
                                if (admission.status === "FULFILLED") {
                                    if (
                                        offer.tripCoverage?.coveredTripId === allTripWithoutExchange[0]?.id
                                    ) {
                                        outboundConfirmedPrice += calculatePrice(admission.price);
                                    } else if (
                                        bookedOffers.length > 1 &&
                                        offer.tripCoverage?.coveredTripId === allTripWithoutExchange[1]?.id
                                    ) {
                                        inboundConfirmedPrice += calculatePrice(admission.price);
                                    }
                                }
                            });
                        });

                        // Helper function to get passenger IDs by status
                        const getPassengerIdsByStatus = (offer: any, status: string) => {
                            return (
                                offer?.admissions
                                    ?.filter((a: any) => a.status === status)
                                    ?.flatMap((a: any) => a.passengerIds) || []
                            );
                        };

                        // Helper function to check if all admissions have the same status
                        const areAllAdmissionsSameStatus = (admissions: any[], status: string) => {
                            return (
                                admissions.length > 0 &&
                                admissions.every((a: any) => a.status === status)
                            );
                        };


                        // Helper to collect admission ids for fulfilled admissions
                        const getFulfillmentAdmissionIdsFromOffer = (offer: any): string[] => {
                            if (!offer || !offer.admissions) return [];
                            return offer.admissions
                                .filter((admission: any) => admission.status === "FULFILLED" && admission?.id)
                                .map((admission: any) => admission.id);
                        };

                        const resolveOfferStatus = (admissions: any[], fallbackStatus: string = "UNKNOWN") => {
                            if (!admissions || admissions.length === 0) {
                                return fallbackStatus;
                            }

                            if (admissions.every((a: any) => a.status === "REFUNDED" || a.status === "RELEASED")) {
                                return "REFUNDED";
                            }

                            if (admissions.some((a: any) => a.status === "FULFILLED" || a.status === "EXCHANGE_ONGOING")) {
                                return "FULFILLED";
                            }

                            if (admissions.some((a: any) => a.status === "ON_HOLD")) {
                                return "ON_HOLD";
                            }

                            if (admissions.some((a: any) => a.status === "EXCHANGED")) {
                                return "EXCHANGED";
                            }

                            return admissions[0]?.status || fallbackStatus;
                        };

                        // Helper function to create a booking for specific passengers
                        const createBookingForPassengers = (
                            tripDataForOutboundLeg: any,    // The specific trip object for the outbound leg (e.g., from booking.trips)
                            offerDataForOutboundLeg: any,  // The offer object for the outbound leg
                            passengerIdsToIncludeInResolvedList: string[], // Final list of (non-exchanged) passenger IDs for the .passengers array
                            status: string = "FULFILLED",
                            inboundLegDetails?: {            // Optional bundle for inbound
                                trip: any;                   // Inbound trip object
                                offer: any;                  // Inbound offer object
                            }
                        ) => {
                            // Parameters renamed for clarity in this scope:
                            const primaryTripData = tripDataForOutboundLeg;
                            const primaryOfferData = offerDataForOutboundLeg;

                            //const bookingType = inboundLegDetails ? "roundTrip" : "oneWayTrip";
                            const hasOutboundDirForPair = (primaryTripData?.direction === TripTypeEnum.OUT_BOUND) || (inboundLegDetails?.trip?.direction === TripTypeEnum.OUT_BOUND);
                            const hasInboundDirForPair = (primaryTripData?.direction === TripTypeEnum.IN_BOUND) || (inboundLegDetails?.trip?.direction === TripTypeEnum.IN_BOUND);
                            const bookingType = hasOutboundDirForPair && hasInboundDirForPair ? "roundTrip" : "oneWayTrip";

                            const currentTripIds: string[] = [primaryTripData.id];
                            if (inboundLegDetails) {
                                currentTripIds.push(inboundLegDetails.trip.id);
                            }

                            // Initialize passengerData
                            const currentPassengerData: { outbound: string[]; inbound: string[] } = {
                                outbound: [],
                                inbound: [],
                            };

                            if (primaryTripData.direction === TripTypeEnum.OUT_BOUND) {
                                currentPassengerData.outbound = nonExchangedPassengerIds(primaryOfferData);
                                
                                if (inboundLegDetails) {
                                    currentPassengerData.inbound = nonExchangedPassengerIds(inboundLegDetails.offer);
                                } else {
                                    const originalInboundTrip = booking.trips.find((t: any) => t.id !== primaryTripData.id && t.direction === TripTypeEnum.IN_BOUND);
                                    if (originalInboundTrip) {
                                        const originalInboundOffer = booking.bookedOffers.find((o: any) => o.tripCoverage?.coveredTripId === originalInboundTrip.id);
                                        if (originalInboundOffer) {
                                            currentPassengerData.inbound = nonExchangedPassengerIds(originalInboundOffer);
                                        }
                                    }
                                }
                            } else if (primaryTripData.direction === TripTypeEnum.IN_BOUND) {
                                currentPassengerData.inbound = nonExchangedPassengerIds(primaryOfferData);
                                
                                const originalOutboundTrip = booking.trips.find((t: any) => t.id !== primaryTripData.id && t.direction === TripTypeEnum.OUT_BOUND);
                                if (originalOutboundTrip) {
                                    const originalOutboundOffer = booking.bookedOffers.find((o: any) => o.tripCoverage?.coveredTripId === originalOutboundTrip.id);
                                    if (originalOutboundOffer) {
                                        currentPassengerData.outbound = nonExchangedPassengerIds(originalOutboundOffer);
                                    }
                                }
                            } else {
                                // Fallback for safety, though trip directions should always be OUT_BOUND or IN_BOUND
                                // If primaryTripData doesn't have a direction or it's unexpected, 
                                // passengerData.outbound might be from primaryOfferData by default if it makes sense contextually, or remain empty.
                                // For now, assume primaryOfferData corresponds to outbound if direction is unknown.
                                currentPassengerData.outbound = nonExchangedPassengerIds(primaryOfferData);
                            }

                            const resolvedPassengers = booking.passengers
                                .filter((p: IPassenger) => passengerIdsToIncludeInResolvedList.includes(p.id))
                                .map(toCardPassenger);

                            let finalInboundDisplayData: any = undefined;
                            if (bookingType === "roundTrip" && inboundLegDetails) {
                                finalInboundDisplayData = {
                                    fromName: stopManager.getStopNameById(inboundLegDetails.trip.origin.stopPlaceRef),
                                    toName: stopManager.getStopNameById(inboundLegDetails.trip.destination.stopPlaceRef),
                                    from: inboundLegDetails.trip.origin.stopPlaceRef,
                                    to: inboundLegDetails.trip.destination.stopPlaceRef,
                                    duration: convertISO8601ToDuration(inboundLegDetails.trip.duration),
                                    stops: inboundLegDetails.trip.transfers > 0 ? Array(inboundLegDetails.trip.transfers).fill("Stop") : [],
                                    departureTime: convertTimeToArabic(inboundLegDetails.trip.startTime),
                                    arrivalTime: convertTimeToArabic(inboundLegDetails.trip.endTime),
                                    arrivalDateTime: inboundLegDetails.trip.endTime,
                                    departureDateTime: inboundLegDetails.trip.startTime,
                                    status: "FULFILLED", // Assuming part of a fulfilled round trip
                                    offerId: inboundLegDetails.offer.offerId,
                                    summary: inboundLegDetails.offer.admissions?.[0]?.summary || "",
                                    isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                    inboundConfirmedPrice: inboundConfirmedPrice,
                                    selectedPriceClass: getTrainClassTypeBaseOnProduct(inboundLegDetails?.offer?.products || []) || "",
                                    productName: getProductNameFromOffer(inboundLegDetails?.offer),
                                    fulfillmentIds: getFulfillmentAdmissionIdsFromOffer(inboundLegDetails.offer),
                                    // Include other fields for inboundData if necessary from existing logic
                                };
                            }

                            return {
                                ...bookingTrip, // Spread original bookingTrip from outer scope for base properties
                                bookingId: bookingTrip.id, // Ensure this is the original booking ID
                                fromName: stopManager.getStopNameById(tripDataForOutboundLeg.origin.stopPlaceRef),
                                toName: stopManager.getStopNameById(tripDataForOutboundLeg.destination.stopPlaceRef),
                                from: tripDataForOutboundLeg.origin.stopPlaceRef,
                                to: tripDataForOutboundLeg.destination.stopPlaceRef,
                                departureTime: convertTimeToArabic(tripDataForOutboundLeg.startTime),
                                arrivalTime: convertTimeToArabic(tripDataForOutboundLeg.endTime),
                                class: offerDataForOutboundLeg?.admissions?.[0]?.summary?.split(" ")[0] || "",
                                summary: offerDataForOutboundLeg?.admissions?.[0]?.summary || "",
                                isOutboundIsDirectTrain: !offerDataForOutboundLeg?.admissions?.[0]?.summary?.toLowerCase().includes("shuttle"),
                                duration: convertISO8601ToDuration(tripDataForOutboundLeg.duration),
                                trainNumber: booking.bookingCode, // Use code from original bookingTrip context
                                stops: tripDataForOutboundLeg.transfers > 0 ? Array(tripDataForOutboundLeg.transfers).fill("Stop") : [],
                                status: status,
                                startDate: tripDataForOutboundLeg.startTime ? new Date(tripDataForOutboundLeg.startTime).toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short" }) : "",
                                startDateFormat: tripDataForOutboundLeg.startTime ? getShortDateFromDateStringFormatted(tripDataForOutboundLeg.startTime) : "",
                                arrivalDateTime: tripDataForOutboundLeg.endTime,
                                departureDateTime: tripDataForOutboundLeg.startTime,
                                bookingType: bookingType,
                                outBoundOfferID: offerDataForOutboundLeg?.offerId ?? null,
                                selectedPriceClass: getTrainClassTypeBaseOnProduct(offerDataForOutboundLeg?.products || []) || "",
                                productName: getProductNameFromOffer(offerDataForOutboundLeg),
                                inboundOfferID: inboundLegDetails?.offer?.offerId ?? null,
                                inboundData: finalInboundDisplayData, // This is the display object for inbound
                                // repeatBooking: true, // Retain from ...bookingTrip or set explicitly if needed
                                passengers: resolvedPassengers, // Populated as before, but from the correct ID list

                                // New fields
                                tripIds: currentTripIds,
                                passengerData: currentPassengerData,
                                isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                fulfillmentIds: getFulfillmentAdmissionIdsFromOffer(primaryOfferData),
                                noModificationOnBooking: noModificationOnBooking,
                                outboundConfirmedPrice: outboundConfirmedPrice,
                                inboundConfirmedPrice: inboundConfirmedPrice,
                                isBookingHasShuttle: isBookingHasShuttle,
                                    isShuttleOnlyBooking: isShuttleOnlyBooking,
                                confirmationTimeLimit: booking.confirmationTimeLimit,
                                provisionalPrice: calculatePrice(booking.provisionalPrice),
                                quickExchangedirection: primaryTripData?.direction
                            };
                        };

                        // Handle REFUNDED case logic
                        const handleRefundedOffer = (trip: any, offer: any) => {
                            const allAdmissionStatuses =
                                offer?.admissions?.map((a: any) => a.status) || [];

                            if (allAdmissionStatuses.every((status: any) => status === "REFUNDED" || status === "RELEASED")) {
                                const allPassengerIds =
                                    offer?.admissions?.flatMap((a: any) => a.passengerIds) || [];
                                return createBookingForPassengers(
                                    trip,       // outboundTripData
                                    offer,      // outboundOfferData
                                    allPassengerIds, // passengerIdsToInclude (all for refunded)
                                    "REFUNDED"
                                );
                            }
                            const fulfilledPassengerIds = getPassengerIdsByStatus(
                                offer,
                                "FULFILLED",
                            );
                            if (fulfilledPassengerIds.length > 0) {
                                // For mixed FULFILLED/REFUNDED, only FULFILLED part is processed here before.
                                // The new requirement is to list ALL non-exchanged passengers if *any* part is fulfilled.
                                const nonExchangedPaxIds = nonExchangedPassengerIds(offer);
                                return createBookingForPassengers(
                                    trip,
                                    offer,
                                    nonExchangedPaxIds,
                                    "FULFILLED"
                                );
                            }
                            return null;
                        };

                        // Helper function to check if booking contains shuttle services
                        const hasShuttleServices = (bookedOffers: any[]): boolean =>
                            bookedOffers.some(offerTouchesShuttle);

                        // Handle split round trip case dynamically
                        const handleSplitRoundTrip = (outboundTrips: any[], inboundTrips: any[], bookedOffers: any[]): IBookedTrips[] => {
                            try {
                                // Always group offers by trip ID first to handle connecting trains properly
                                const offersByTrip = new Map<string, any[]>();
                                bookedOffers.forEach(offer => {
                                    const tripId = offer.tripCoverage?.coveredTripId;
                                    if (tripId) {
                                        if (!offersByTrip.has(tripId)) {
                                            offersByTrip.set(tripId, []);
                                        }
                                        offersByTrip.get(tripId)!.push(offer);
                                    }
                                });

                                const collectPassengerIdsForTrip = (trip: any): string[] => {
                                    if (!trip) {
                                        return [];
                                    }
                                    const tripOffers = offersByTrip.get(trip.id) || [];
                                    return Array.from(new Set(
                                        tripOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                    ));
                                };

                                const getTripPassengerGroupKey = (trip: any): string => {
                                    if (!trip) {
                                        return "";
                                    }
                                    const passengerIds = collectPassengerIdsForTrip(trip);
                                    const passengerSignature = passengerIds?.length > 0
                                        ? passengerIds?.sort()?.join("|")
                                        : trip.id;
                                    const directionLabel = trip.direction || "";
                                    return `${directionLabel}:${passengerSignature}`;
                                };

                                // --- CDT partial-passenger splitting ---
                                // Detects offers created by a Change-Date-Time exchange that only
                                // covered a subset of passengers, then groups offers by their
                                // non-exchanged passenger set so each group gets its own card.
                                const isCdtSplitOffer = (offer: any): boolean => {
                                    if (!offer || !offer.admissions) return false;
                                    const totalPassengers = booking.passengers?.length || 0;
                                    if (totalPassengers <= 1) return false;

                                    // Offer covers fewer passengers than the full booking
                                    // Use ALL passenger IDs (including EXCHANGED) to determine coverage
                                    const allOfferPassengerIds = Array.from(new Set(
                                        offer.admissions.flatMap((a: any) => a.passengerIds || [])
                                    ));
                                    const isPartialPassengerSet = allOfferPassengerIds.length > 0 && allOfferPassengerIds.length < totalPassengers;

                                    // All admissions are either EXCHANGED or FULFILLED
                                    const allAdmissionsExchangedOrFulfilled = offer.admissions.every(
                                        (a: any) => a.status === "EXCHANGED" || a.status === "FULFILLED"
                                    );

                                    return isPartialPassengerSet && allAdmissionsExchangedOrFulfilled;
                                };

                                // Categorise offers into CDT-split vs original
                                const cdtOffers = bookedOffers.filter(o => isCdtSplitOffer(o));
                                const originalOffers = bookedOffers.filter(o => !isCdtSplitOffer(o));

                                // CDT passenger-group processing
                                if (cdtOffers.length > 0 && outboundTrips.length >= 1 && inboundTrips.length >= 1) {
                                    const allOffersForGrouping = [...originalOffers, ...cdtOffers];

                                    // See cdtGroupingIds (bookingRowRiders).
                                    const movedPassengerIds = movedPassengerIdsOf(bookedOffers);
                                    const getCdtGroupingIds = (offer: any): string[] => cdtGroupingIds(offer, movedPassengerIds);

                                    // Group offers by their non-exchanged passenger ID set
                                    const passengerGroupMap = new Map<string, any[]>();
                                    allOffersForGrouping.forEach(offer => {
                                        const paxIds = getCdtGroupingIds(offer);
                                        if (paxIds.length === 0) return;
                                        const key = [...paxIds].sort().join("|");
                                        if (!passengerGroupMap.has(key)) {
                                            passengerGroupMap.set(key, []);
                                        }
                                        passengerGroupMap.get(key)!.push(offer);
                                    });

                                    const cdtBookings: IBookedTrips[] = [];
                                    const nowDubai = bookingsPastPivotDubai();

                                    const pickPrimaryCdtTrip = (trips: any[]) => {
                                        const nonShuttle = trips.find(t =>
                                            !(offersByTrip.get(t.id) || []).some(isShuttleOffer)
                                        );
                                        return nonShuttle || trips[0];
                                    };

                                    passengerGroupMap.forEach((groupOffers, paxKey) => {
                                        const paxIds = paxKey.split("|");

                                        // Find outbound & inbound trips for this passenger group
                                        const groupOutboundTrips = outboundTrips.filter(t => {
                                            const tripOffers = offersByTrip.get(t.id) || [];
                                            return tripOffers.some(o => {
                                                const ids = getCdtGroupingIds(o);
                                                return paxIds.every(pid => ids.includes(pid));
                                            });
                                        });
                                        const groupInboundTrips = inboundTrips.filter(t => {
                                            const tripOffers = offersByTrip.get(t.id) || [];
                                            return tripOffers.some(o => {
                                                const ids = getCdtGroupingIds(o);
                                                return paxIds.every(pid => ids.includes(pid));
                                            });
                                        });

                                        if (groupOutboundTrips.length === 0 && groupInboundTrips.length === 0) return;

                                        const outTrip = groupOutboundTrips.length > 0 ? pickPrimaryCdtTrip(groupOutboundTrips) : null;
                                        const inTrip = groupInboundTrips.length > 0 ? pickPrimaryCdtTrip(groupInboundTrips) : null;

                                        // Collect offers specific to this group's trips
                                        const groupOutOffers = groupOutboundTrips.flatMap(t => offersByTrip.get(t.id) || [])
                                            .filter(o => {
                                                const ids = getCdtGroupingIds(o);
                                                return paxIds.every(pid => ids.includes(pid));
                                            });
                                        const groupInOffers = groupInboundTrips.flatMap(t => offersByTrip.get(t.id) || [])
                                            .filter(o => {
                                                const ids = getCdtGroupingIds(o);
                                                return paxIds.every(pid => ids.includes(pid));
                                            });

                                        const pickNonShuttleOffer = (offers: any[]) => offers.find(o => !isShuttleOffer(o));

                                        if (outTrip && inTrip) {
                                            const outDubai = dayjs(outTrip.startTime).tz("Asia/Dubai");
                                            const inDubai = dayjs(inTrip.startTime).tz("Asia/Dubai");
                                            const isOutPast = outDubai.isBefore(nowDubai);
                                            const isInPast = inDubai.isBefore(nowDubai);

                                            if (isOutPast !== isInPast) {
                                                // Split into two one-way cards
                                                const primaryOutOffer = pickNonShuttleOffer(groupOutOffers) || groupOutOffers[0];
                                                const outAdmissions = groupOutOffers.flatMap(o => o.admissions || [])
                                                    .filter((a: any) => a.status !== "EXCHANGED" && a.passengerIds?.some((pid: string) => paxIds.includes(pid)));
                                                const outStatus = resolveOfferStatus(outAdmissions, "FULFILLED");
                                                const outCard = createBookingForPassengers(outTrip, primaryOutOffer, paxIds, outStatus);
                                                outCard.direction = TripTypeEnum.OUT_BOUND;
                                                outCard.fulfillmentIds = Array.from(new Set(groupOutOffers.flatMap(o => getFulfillmentAdmissionIdsFromOffer(o))));
                                                cdtBookings.push(outCard);

                                                const primaryInOffer = pickNonShuttleOffer(groupInOffers) || groupInOffers[0];
                                                const inAdmissions = groupInOffers.flatMap(o => o.admissions || [])
                                                    .filter((a: any) => a.status !== "EXCHANGED" && a.passengerIds?.some((pid: string) => paxIds.includes(pid)));
                                                const inStatus = resolveOfferStatus(inAdmissions, "FULFILLED");
                                                const inCard = createBookingForPassengers(inTrip, primaryInOffer, paxIds, inStatus);
                                                inCard.direction = TripTypeEnum.IN_BOUND;
                                                inCard.fulfillmentIds = Array.from(new Set(groupInOffers.flatMap(o => getFulfillmentAdmissionIdsFromOffer(o))));
                                                cdtBookings.push(inCard);
                                            } else {
                                                // Same time period — single round trip card
                                                const primaryOutOffer = pickNonShuttleOffer(groupOutOffers) || groupOutOffers[0];
                                                const primaryInOffer = pickNonShuttleOffer(groupInOffers) || groupInOffers[0];
                                                const allAdmissions = [...groupOutOffers, ...groupInOffers]
                                                    .flatMap(o => o.admissions || [])
                                                    .filter((a: any) => a.status !== "EXCHANGED" && a.passengerIds?.some((pid: string) => paxIds.includes(pid)));
                                                const status = resolveOfferStatus(allAdmissions, "FULFILLED");

                                                const rtCard = createBookingForPassengers(
                                                    outTrip, primaryOutOffer, paxIds, status,
                                                    { trip: inTrip, offer: primaryInOffer }
                                                );
                                                rtCard.fulfillmentIds = Array.from(new Set(groupOutOffers.flatMap(o => getFulfillmentAdmissionIdsFromOffer(o))));
                                                if (rtCard.inboundData) {
                                                    rtCard.inboundData.fulfillmentIds = Array.from(new Set(groupInOffers.flatMap(o => getFulfillmentAdmissionIdsFromOffer(o))));
                                                }
                                                cdtBookings.push(rtCard);
                                            }
                                        } else if (outTrip) {
                                            // Only outbound direction found
                                            const primaryOffer = pickNonShuttleOffer(groupOutOffers) || groupOutOffers[0];
                                            const admissions = groupOutOffers.flatMap(o => o.admissions || [])
                                                .filter((a: any) => a.status !== "EXCHANGED" && a.passengerIds?.some((pid: string) => paxIds.includes(pid)));
                                            const status = resolveOfferStatus(admissions, "FULFILLED");
                                            const card = createBookingForPassengers(outTrip, primaryOffer, paxIds, status);
                                            card.direction = TripTypeEnum.OUT_BOUND;
                                            card.fulfillmentIds = Array.from(new Set(groupOutOffers.flatMap(o => getFulfillmentAdmissionIdsFromOffer(o))));
                                            cdtBookings.push(card);
                                        } else if (inTrip) {
                                            // Only inbound direction found
                                            const primaryOffer = pickNonShuttleOffer(groupInOffers) || groupInOffers[0];
                                            const admissions = groupInOffers.flatMap(o => o.admissions || [])
                                                .filter((a: any) => a.status !== "EXCHANGED" && a.passengerIds?.some((pid: string) => paxIds.includes(pid)));
                                            const status = resolveOfferStatus(admissions, "FULFILLED");
                                            const card = createBookingForPassengers(inTrip, primaryOffer, paxIds, status);
                                            card.direction = TripTypeEnum.IN_BOUND;
                                            card.fulfillmentIds = Array.from(new Set(groupInOffers.flatMap(o => getFulfillmentAdmissionIdsFromOffer(o))));
                                            cdtBookings.push(card);
                                        }
                                    });

                                    if (cdtBookings.length > 0) {
                                        return cdtBookings;
                                    }
                                    // Fall through to existing logic if no CDT cards were created
                                }

                                // Handle round trips - check if legs should be split based on time
                                // Also handle cases where outbound and/or inbound have multiple trips (train + shuttle)
                                if (outboundTrips.length >= 1 && inboundTrips.length >= 1) {
                                    // For multiple outbound trips (e.g., train + shuttle), pick the primary (non-shuttle) trip
                                    const pickPrimaryTrip = (trips: any[]) => {
                                        // Prefer non-shuttle trips
                                        const nonShuttleTrip = trips.find(t =>
                                            !(offersByTrip.get(t.id) || []).some(isShuttleOffer)
                                        );
                                        return nonShuttleTrip || trips[0];
                                    };

                                    const outboundTrip = pickPrimaryTrip(outboundTrips);
                                    const inboundTrip = pickPrimaryTrip(inboundTrips);

                                    // Collect offers from ALL outbound trips (including shuttle) and ALL inbound trips
                                    const outboundOffers = outboundTrips.flatMap(t => offersByTrip.get(t.id) || []);
                                    const inboundOffers = inboundTrips.flatMap(t => offersByTrip.get(t.id) || []);

                                    if (outboundOffers.length > 0 && inboundOffers.length > 0) {
                                        // Check if this booking has shuttle services
                                        const allOffers = [...outboundOffers, ...inboundOffers];
                                        const hasShuttleInBooking = allOffers.some(offerTouchesShuttle);

                                        // Check if legs are in different time periods (past vs future)
                                        const nowDubai = bookingsPastPivotDubai();
                                        const outboundDubai = dayjs(outboundTrip.startTime).tz("Asia/Dubai");
                                        const inboundDubai = dayjs(inboundTrip.startTime).tz("Asia/Dubai");
                                        const isOutboundPast = outboundDubai.isBefore(nowDubai);
                                        const isInboundPast = inboundDubai.isBefore(nowDubai);

                                        // If one leg is past and one is future, split into separate one-way trips
                                        // BUT NOT for shuttle bookings - keep them as round trip
                                        if (isOutboundPast !== isInboundPast && !hasShuttleInBooking) {
                                            const bookings: any[] = [];

                                            // Get passenger IDs for each leg (non-EXCHANGED: includes FULFILLED, REFUNDED, RELEASED)
                                            // This ensures we show all passengers even if some are REFUNDED
                                            const outboundPassengerIds = Array.from(new Set(
                                                outboundOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                            ));
                                            const inboundPassengerIds = Array.from(new Set(
                                                inboundOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                            ));

                                            // Create outbound one-way trip
                                            if (outboundPassengerIds.length > 0) {
                                                // Filter admissions to only those for passengers we're including (non-EXCHANGED)
                                                const outboundAdmissions = outboundOffers.flatMap(o => o.admissions || [])
                                                    .filter((a: any) => 
                                                        a.status !== "EXCHANGED" && 
                                                        a.passengerIds?.some((pid: string) => outboundPassengerIds.includes(pid))
                                                    );
                                                const outboundStatus = resolveOfferStatus(outboundAdmissions, "FULFILLED");

                                                const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                                const primaryOutboundOffer = pickNonShuttle(outboundOffers) || outboundOffers[0];

                                                // Create outbound as a one-way trip (no inboundLegDetails)
                                                const outboundBooking = createBookingForPassengers(
                                                    outboundTrip,
                                                    primaryOutboundOffer,
                                                    outboundPassengerIds,
                                                    outboundStatus
                                                    // Note: No inboundLegDetails parameter - this creates a one-way trip
                                                );
                                                outboundBooking.direction = TripTypeEnum.OUT_BOUND;

                                                // Collect fulfillment IDs only from outbound offers (already filtered by trip)
                                                const outboundFulfillmentIds = Array.from(new Set(
                                                    outboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                                ));
                                                
                                                outboundBooking.fulfillmentIds = outboundFulfillmentIds;
                                                bookings.push(outboundBooking);
                                            }

                                            // Create inbound one-way trip
                                            if (inboundPassengerIds.length > 0) {
                                                // Filter admissions to only those for passengers we're including (non-EXCHANGED)
                                                const inboundAdmissions = inboundOffers.flatMap(o => o.admissions || [])
                                                    .filter((a: any) => 
                                                        a.status !== "EXCHANGED" && 
                                                        a.passengerIds?.some((pid: string) => inboundPassengerIds.includes(pid))
                                                    );
                                                const inboundStatus = resolveOfferStatus(inboundAdmissions, "FULFILLED");

                                                const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                                const primaryInboundOffer = pickNonShuttle(inboundOffers) || inboundOffers[0];

                                                // Create inbound as a one-way trip (no inboundLegDetails)
                                                const inboundBooking = createBookingForPassengers(
                                                    inboundTrip,
                                                    primaryInboundOffer,
                                                    inboundPassengerIds,
                                                    inboundStatus
                                                    // Note: No inboundLegDetails parameter - this creates a one-way trip
                                                );
                                                inboundBooking.direction = TripTypeEnum.IN_BOUND;
                                                // Collect fulfillment IDs only from inbound offers (already filtered by trip)
                                                const inboundFulfillmentIds = Array.from(new Set(
                                                    inboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                                ));
                                                inboundBooking.fulfillmentIds = inboundFulfillmentIds;
                                                bookings.push(inboundBooking);
                                            }

                                            return bookings;
                                        } else {
                                            // Both legs are in same time period OR it's a shuttle booking (don't split)
                                            // Only include passengers who have FULFILLED status in BOTH legs
                                            const fulfilledOutboundPassengerIds = Array.from(new Set(
                                                outboundOffers.flatMap(offer => getPassengerIdsByStatus(offer, "FULFILLED"))
                                            ));
                                            const fulfilledInboundPassengerIds = Array.from(new Set(
                                                inboundOffers.flatMap(offer => getPassengerIdsByStatus(offer, "FULFILLED"))
                                            ));

                                            // Only passengers who are FULFILLED in BOTH legs form a round trip
                                            let commonPassengerIds = fulfilledOutboundPassengerIds.filter((id: string) =>
                                                fulfilledInboundPassengerIds.includes(id)
                                            );

                                            /**
                                             * A WHOLLY CANCELLED round trip keeps rendering as the single card it
                                             * was.
                                             *
                                             * Once every admission is REFUNDED or RELEASED the FULFILLED intersection
                                             * above is empty, and this used to `return []`. The caller reads that as "not a
                                             * round trip", sets `shouldProcessIndividually` and emits ONE CARD PER
                                             * TRIP: booking 62KQNE/4UJJHA exploded into four /bookings rows (two
                                             * trains + two shuttles) all carrying the same reference.
                                             *
                                             * REFUNDED means "cancelled but still on this booking", the same
                                             * distinction `tripsStillHeld` draws — only EXCHANGED is gone, having
                                             * been replaced by a new offer that renders in its own right. So fall
                                             * back to the passengers still carried on both legs, with the cancelled
                                             * status `resolveOfferStatus` derives below.
                                             *
                                             * CANCELLED, not merely "nothing FULFILLED" — and the difference is
                                             * load-bearing, not defensive. ON_HOLD and PREBOOKED-only bookings also
                                             * have nothing FULFILLED and also reach here (`hasFulfilledAdmission`
                                             * admits ON_HOLD); the looser test pulled unpaid holds into the list as
                                             * ordinary Upcoming trips, where before they produced no card at all.
                                             * This gate is the only thing keeping them out. Do not relax it.
                                             *
                                             * A PARTIAL cancellation cannot reach the widening either: the live leg
                                             * still holds a FULFILLED admission, so it must keep its own row rather
                                             * than be folded into a card that hides which half was cancelled.
                                             *
                                             * A leg mixing REFUNDED with EXCHANGED still qualifies — the EXCHANGED
                                             * part is excluded below and renders as its own offer.
                                             */
                                            const legAdmissions = [...outboundOffers, ...inboundOffers]
                                                .flatMap(o => o.admissions || [])
                                                .filter((a: any) => a.status !== "EXCHANGED");
                                            const everyAdmissionCancelled =
                                                legAdmissions.length > 0 &&
                                                legAdmissions.every((a: any) =>
                                                    a.status === "REFUNDED" || a.status === "RELEASED");

                                            if (commonPassengerIds.length === 0 && everyAdmissionCancelled) {
                                                const carriedOutboundPassengerIds = Array.from(new Set(
                                                    outboundOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                                ));
                                                const carriedInboundPassengerIds = Array.from(new Set(
                                                    inboundOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                                ));
                                                commonPassengerIds = carriedOutboundPassengerIds.filter((id: string) =>
                                                    carriedInboundPassengerIds.includes(id)
                                                );
                                            }

                                            if (commonPassengerIds.length === 0) {
                                                return [];
                                            }

                                            const outboundAdmissions = outboundOffers.flatMap(o => o.admissions || []).filter((a: any) =>
                                                a.status !== "EXCHANGED" && commonPassengerIds.some((pid: string) => a.passengerIds?.includes(pid))
                                            );
                                            const inboundAdmissions = inboundOffers.flatMap(o => o.admissions || []).filter((a: any) =>
                                                a.status !== "EXCHANGED" && commonPassengerIds.some((pid: string) => a.passengerIds?.includes(pid))
                                            );
                                            const combinedAdmissions = [...outboundAdmissions, ...inboundAdmissions];
                                            const overallStatus = resolveOfferStatus(combinedAdmissions, "FULFILLED");

                                            const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                            const primaryOutboundOffer = pickNonShuttle(outboundOffers) || outboundOffers[0];
                                            const primaryInboundOffer = pickNonShuttle(inboundOffers) || inboundOffers[0];

                                            const roundTrip = createBookingForPassengers(
                                                outboundTrip,
                                                primaryOutboundOffer,
                                                commonPassengerIds,
                                                overallStatus,
                                                { trip: inboundTrip, offer: primaryInboundOffer }
                                            );
                                            // Collect fulfillment IDs from all outbound offers
                                            roundTrip.fulfillmentIds = Array.from(new Set(
                                                outboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                            ));
                                            if (roundTrip.inboundData) {
                                                roundTrip.inboundData.fulfillmentIds = Array.from(new Set(
                                                    inboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                                ));
                                            }
                                            return [roundTrip];
                                        }
                                    }
                                }

                                // Handle one-way trips (both outbound and inbound)
                                if (outboundTrips.length > 0 && inboundTrips.length === 0) {
                                    // Group trips by shared passenger IDs to merge shuttle + train trips
                                    const tripGroups: Map<string, { trips: any[], passengerIds: Set<string>, travellerIds: Set<string>, allOffers: any[] }> = new Map();
                                    
                                    for (const outboundTrip of outboundTrips) {
                                        const tripOffers = offersByTrip.get(outboundTrip.id) || [];
                                        if (tripOffers.length === 0) continue;

                                        const tripPassengerIds = new Set(tripOffers.flatMap(offer => nonExchangedPassengerIds(offer)));
                                        const tripTravellerIds = groupingPassengerIdsForTrip(tripOffers);
                                        if (tripPassengerIds.size === 0) continue;

                                        // Find existing group with matching passenger IDs
                                        let foundGroup = false;
                                        for (const [groupId, group] of tripGroups.entries()) {
                                            // Check if passenger IDs overlap (trips share passengers)
                                            const hasOverlap = Array.from(tripTravellerIds).some(id => group.travellerIds.has(id));
                                            if (hasOverlap) {
                                                // Merge into existing group
                                                group.trips.push(outboundTrip);
                                                tripPassengerIds.forEach(id => group.passengerIds.add(id));
                                                tripTravellerIds.forEach(id => group.travellerIds.add(id));
                                                group.allOffers.push(...tripOffers);
                                                foundGroup = true;
                                                break;
                                            }
                                        }

                                        // If no matching group found, create new one
                                        if (!foundGroup) {
                                            const groupId = Array.from(tripPassengerIds).sort().join(',');
                                            tripGroups.set(groupId, {
                                                trips: [outboundTrip],
                                                passengerIds: new Set(tripPassengerIds),
                                                travellerIds: new Set(tripTravellerIds),
                                                allOffers: [...tripOffers]
                                            });
                                        }
                                    }

                                    // Process each group into a single booking
                                    const processedTrips: any[] = [];
                                    for (const group of tripGroups.values()) {
                                        const allPassengerIds = Array.from(group.passengerIds).filter(id => !wasMovedOffGroup(id, group.allOffers));
                                        const allAdmissions = group.allOffers.flatMap(o => o.admissions || []);
                                        const overallStatus = resolveOfferStatus(allAdmissions, "FULFILLED");

                                        // Sort trips by start time to get the correct order (shuttle before train typically)
                                        const sortedTrips = [...group.trips].sort((a, b) => 
                                            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
                                        );
                                        
                                        // Use the first trip as primary, but prefer non-shuttle trip if available for display
                                        const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                        const primaryOffer = pickNonShuttle(group.allOffers);
                                        
                                        // Find primary trip - prefer trip with non-shuttle offer, otherwise use first trip
                                        let primaryTrip = sortedTrips[0];
                                        if (primaryOffer) {
                                            const tripWithPrimaryOffer = sortedTrips.find(t => 
                                                group.allOffers.some(o => 
                                                    o.offerId === primaryOffer.offerId && 
                                                    o.tripCoverage?.coveredTripId === t.id
                                                )
                                            );
                                            if (tripWithPrimaryOffer) {
                                                primaryTrip = tripWithPrimaryOffer;
                                            }
                                        }

                                        // Use the first trip's origin and last trip's destination for merged display
                                        const firstTrip = sortedTrips[0];
                                        const lastTrip = sortedTrips[sortedTrips.length - 1];
                                        
                                        // Create booking using primary trip but update origin/destination to cover all trips
                                        const merged = createBookingForPassengers(
                                            primaryTrip,
                                            primaryOffer || group.allOffers[0],
                                            allPassengerIds,
                                            overallStatus
                                        );
                                        
                                        // Update origin/destination to reflect merged journey (first trip origin, last trip destination)
                                        merged.from = firstTrip.origin?.stopPlaceRef || primaryTrip.origin?.stopPlaceRef;
                                        merged.to = lastTrip.destination?.stopPlaceRef || primaryTrip.destination?.stopPlaceRef;
                                        merged.fromName = stopManager.getStopNameById(merged.from);
                                        merged.toName = stopManager.getStopNameById(merged.to);
                                        merged.departureDateTime = firstTrip.startTime;
                                        merged.arrivalDateTime = lastTrip.endTime;
                                        merged.departureTime = convertTimeToArabic(firstTrip.startTime);
                                        merged.arrivalTime = convertTimeToArabic(lastTrip.endTime);
                                        
                                        // Calculate total duration from first trip start to last trip end
                                        if (firstTrip.startTime && lastTrip.endTime) {
                                            const startDate = new Date(firstTrip.startTime);
                                            const endDate = new Date(lastTrip.endTime);
                                            const totalDurationMs = endDate.getTime() - startDate.getTime();
                                            const totalDurationSeconds = Math.floor(totalDurationMs / 1000);
                                            const hours = Math.floor(totalDurationSeconds / 3600);
                                            const minutes = Math.floor((totalDurationSeconds % 3600) / 60);
                                            
                                            // Format duration to match convertISO8601ToDuration output format
                                            let durationParts: string[] = [];
                                            if (hours > 0) {
                                                durationParts.push(`${hours}h`);
                                            }
                                            if (minutes > 0) {
                                                durationParts.push(`${minutes}min`);
                                            }
                                            merged.duration = durationParts.join(' ') || '0min';
                                        }
                                        
                                        // Update start date to use first trip's start time
                                        merged.startDate = firstTrip.startTime ? new Date(firstTrip.startTime).toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short" }) : merged.startDate;
                                        merged.startDateFormat = firstTrip.startTime ? getShortDateFromDateStringFormatted(firstTrip.startTime) : merged.startDateFormat;
                                        
                                        // Collect fulfillment IDs from all offers in all trips
                                        merged.fulfillmentIds = Array.from(new Set(
                                            group.allOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                        ));
                                        
                                        // Update tripIds to include all merged trips
                                        merged.tripIds = sortedTrips.map(t => t.id);
                                        
                                        processedTrips.push(merged);
                                    }

                                    if (processedTrips.length > 0) {
                                        return processedTrips;
                                    }
                                }

                                if (inboundTrips.length > 0 && outboundTrips.length === 0) {
                                    // Group trips by shared passenger IDs to merge shuttle + train trips
                                    const tripGroups: Map<string, { trips: any[], passengerIds: Set<string>, travellerIds: Set<string>, allOffers: any[] }> = new Map();
                                    
                                    for (const inboundTrip of inboundTrips) {
                                        const tripOffers = offersByTrip.get(inboundTrip.id) || [];
                                        if (tripOffers.length === 0) continue;

                                        const tripPassengerIds = new Set(tripOffers.flatMap(offer => nonExchangedPassengerIds(offer)));
                                        const tripTravellerIds = groupingPassengerIdsForTrip(tripOffers);
                                        if (tripPassengerIds.size === 0) continue;

                                        // Find existing group with matching passenger IDs
                                        let foundGroup = false;
                                        for (const [groupId, group] of tripGroups.entries()) {
                                            // Check if passenger IDs overlap (trips share passengers)
                                            const hasOverlap = Array.from(tripTravellerIds).some(id => group.travellerIds.has(id));
                                            if (hasOverlap) {
                                                // Merge into existing group
                                                group.trips.push(inboundTrip);
                                                tripPassengerIds.forEach(id => group.passengerIds.add(id));
                                                tripTravellerIds.forEach(id => group.travellerIds.add(id));
                                                group.allOffers.push(...tripOffers);
                                                foundGroup = true;
                                                break;
                                            }
                                        }

                                        // If no matching group found, create new one
                                        if (!foundGroup) {
                                            const groupId = Array.from(tripPassengerIds).sort().join(',');
                                            tripGroups.set(groupId, {
                                                trips: [inboundTrip],
                                                passengerIds: new Set(tripPassengerIds),
                                                travellerIds: new Set(tripTravellerIds),
                                                allOffers: [...tripOffers]
                                            });
                                        }
                                    }

                                    // Process each group into a single booking
                                    const processedTrips: any[] = [];
                                    for (const group of tripGroups.values()) {
                                        const allPassengerIds = Array.from(group.passengerIds).filter(id => !wasMovedOffGroup(id, group.allOffers));
                                        const allAdmissions = group.allOffers.flatMap(o => o.admissions || []);
                                        const overallStatus = resolveOfferStatus(allAdmissions, "FULFILLED");

                                        // Sort trips by start time to get the correct order (shuttle before train typically)
                                        const sortedTrips = [...group.trips].sort((a, b) => 
                                            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
                                        );
                                        
                                        // Use the first trip as primary, but prefer non-shuttle trip if available for display
                                        const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                        const primaryOffer = pickNonShuttle(group.allOffers);
                                        
                                        // Find primary trip - prefer trip with non-shuttle offer, otherwise use first trip
                                        let primaryTrip = sortedTrips[0];
                                        if (primaryOffer) {
                                            const tripWithPrimaryOffer = sortedTrips.find(t => 
                                                group.allOffers.some(o => 
                                                    o.offerId === primaryOffer.offerId && 
                                                    o.tripCoverage?.coveredTripId === t.id
                                                )
                                            );
                                            if (tripWithPrimaryOffer) {
                                                primaryTrip = tripWithPrimaryOffer;
                                            }
                                        }

                                        // Use the first trip's origin and last trip's destination for merged display
                                        const firstTrip = sortedTrips[0];
                                        const lastTrip = sortedTrips[sortedTrips.length - 1];
                                        
                                        // Create booking using primary trip but update origin/destination to cover all trips
                                        const merged = createBookingForPassengers(
                                            primaryTrip,
                                            primaryOffer || group.allOffers[0],
                                            allPassengerIds,
                                            overallStatus
                                        );
                                        
                                        // Update origin/destination to reflect merged journey (first trip origin, last trip destination)
                                        merged.from = firstTrip.origin?.stopPlaceRef || primaryTrip.origin?.stopPlaceRef;
                                        merged.to = lastTrip.destination?.stopPlaceRef || primaryTrip.destination?.stopPlaceRef;
                                        merged.fromName = stopManager.getStopNameById(merged.from);
                                        merged.toName = stopManager.getStopNameById(merged.to);
                                        merged.departureDateTime = firstTrip.startTime;
                                        merged.arrivalDateTime = lastTrip.endTime;
                                        merged.departureTime = convertTimeToArabic(firstTrip.startTime);
                                        merged.arrivalTime = convertTimeToArabic(lastTrip.endTime);
                                        
                                        // Calculate total duration from first trip start to last trip end
                                        if (firstTrip.startTime && lastTrip.endTime) {
                                            const startDate = new Date(firstTrip.startTime);
                                            const endDate = new Date(lastTrip.endTime);
                                            const totalDurationMs = endDate.getTime() - startDate.getTime();
                                            const totalDurationSeconds = Math.floor(totalDurationMs / 1000);
                                            const hours = Math.floor(totalDurationSeconds / 3600);
                                            const minutes = Math.floor((totalDurationSeconds % 3600) / 60);
                                            
                                            // Format duration to match convertISO8601ToDuration output format
                                            let durationParts: string[] = [];
                                            if (hours > 0) {
                                                durationParts.push(`${hours}h`);
                                            }
                                            if (minutes > 0) {
                                                durationParts.push(`${minutes}min`);
                                            }
                                            merged.duration = durationParts.join(' ') || '0min';
                                        }
                                        
                                        // Update start date to use first trip's start time
                                        merged.startDate = firstTrip.startTime ? new Date(firstTrip.startTime).toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short" }) : merged.startDate;
                                        merged.startDateFormat = firstTrip.startTime ? getShortDateFromDateStringFormatted(firstTrip.startTime) : merged.startDateFormat;
                                        
                                        // Collect fulfillment IDs from all offers in all trips
                                        merged.fulfillmentIds = Array.from(new Set(
                                            group.allOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                        ));
                                        
                                        // Update tripIds to include all merged trips
                                        merged.tripIds = sortedTrips.map(t => t.id);
                                        
                                        processedTrips.push(merged);
                                    }

                                    if (processedTrips.length > 0) {
                                        return processedTrips;
                                    }
                                }

                                // Handle fully refunded round trip as a single entity to prevent splitting.
                                if (outboundTrips.length > 0 && inboundTrips.length > 0 && bookedOffers.length > 0) {
                                    const allOffersAreRefunded = bookedOffers.every((offer) =>
                                        areAllAdmissionsSameStatus(offer.admissions, "REFUNDED") || areAllAdmissionsSameStatus(offer.admissions, "RELEASED")
                                    );

                                    if (allOffersAreRefunded) {
                                        const outboundTrip = outboundTrips[0];
                                        const inboundTrip = inboundTrips[0];
                                        const outboundOffer = bookedOffers.find(o => o.tripCoverage?.coveredTripId === outboundTrip.id);
                                        const inboundOffer = bookedOffers.find(o => o.tripCoverage?.coveredTripId === inboundTrip.id);

                                        if (outboundOffer && inboundOffer) {
                                            // Check if outbound is past and inbound is future (Asia/Dubai time)
                                            const outboundDate = new Date(outboundTrip.startTime);
                                            const inboundDate = new Date(inboundTrip.startTime);
                                            const nowDubai = bookingsPastPivotDubai();
                                            const outboundDubai = dayjs(outboundDate).tz("Asia/Dubai");
                                            const inboundDubai = dayjs(inboundDate).tz("Asia/Dubai");
                                            const isOutboundPast = outboundDubai.isBefore(nowDubai);
                                            const isInboundPast = inboundDubai.isBefore(nowDubai);

                                            if (isOutboundPast !== isInboundPast) {
                                                // Split into two one-way refunded trips
                                                const outboundPassengerIds = getPassengerIdsByStatus(outboundOffer, "REFUNDED").concat(getPassengerIdsByStatus(outboundOffer, "RELEASED"));
                                                const inboundPassengerIds = getPassengerIdsByStatus(inboundOffer, "REFUNDED").concat(getPassengerIdsByStatus(inboundOffer, "RELEASED"));
                                                const outboundRefunded = createBookingForPassengers(
                                                    outboundTrip,
                                                    outboundOffer,
                                                    outboundPassengerIds,
                                                    "REFUNDED"
                                                );
                                                outboundRefunded.direction = TripTypeEnum.OUT_BOUND;
                                                const inboundRefunded = createBookingForPassengers(
                                                    inboundTrip,
                                                    inboundOffer,
                                                    inboundPassengerIds,
                                                    "REFUNDED"
                                                );
                                                inboundRefunded.direction = TripTypeEnum.IN_BOUND;
                                                return [outboundRefunded, inboundRefunded];
                                            }

                                            // Otherwise, keep as round trip
                                            const allPassengerIds = Array.from(new Set([
                                                ...getPassengerIdsByStatus(outboundOffer, "REFUNDED"),
                                                ...getPassengerIdsByStatus(outboundOffer, "RELEASED"),
                                                ...getPassengerIdsByStatus(inboundOffer, "REFUNDED"),
                                                ...getPassengerIdsByStatus(inboundOffer, "RELEASED")
                                            ]));

                                            const refundedRoundTrip = createBookingForPassengers(
                                                outboundTrip,
                                                outboundOffer,
                                                allPassengerIds,
                                                "REFUNDED",
                                                { trip: inboundTrip, offer: inboundOffer }
                                            );
                                            return [refundedRoundTrip];
                                        }
                                    }
                                }

                                const bookings: IBookedTrips[] = [];
                                const processedOfferIds = new Set<string>();

                                if (!outboundTrips?.length || !inboundTrips?.length) {
                                    return bookings;
                                }

                                // Track processed passenger-trip combinations to handle split bookings correctly
                                const processedPassengerTripCombinations = new Set<string>();
                                
                                // Helper to create a unique key for a passenger-trip combination
                                const getPassengerTripKey = (passengerId: string, outboundTrip: any, inboundTrip: any) => {
                                    const outboundKey = getTripPassengerGroupKey(outboundTrip);
                                    const inboundKey = getTripPassengerGroupKey(inboundTrip);
                                    return `${passengerId}:${outboundKey}:${inboundKey}`;
                                };

                                // Process outbound trips first using grouped offers
                                for (const outboundTrip of outboundTrips) {
                                    const outboundOffers = offersByTrip.get(outboundTrip.id) || [];
                                    if (outboundOffers.length === 0) continue;

                                    // Get FULFILLED passenger IDs from all outbound offers for this trip
                                    const fulfilledOutboundPassengerIds = Array.from(new Set(
                                        outboundOffers.flatMap(offer => getPassengerIdsByStatus(offer, "FULFILLED"))
                                    ));

                                    // If all outbound admissions are REFUNDED, do not form a split round trip
                                    const allOutboundAdmissions = outboundOffers.flatMap(o => o.admissions || []);
                                    if (
                                        fulfilledOutboundPassengerIds.length === 0 &&
                                        allOutboundAdmissions.length > 0 &&
                                        allOutboundAdmissions.every((a: any) => a.status === "REFUNDED" || a.status === "RELEASED")
                                    ) {
                                        continue;
                                    }

                                    if (fulfilledOutboundPassengerIds.length === 0) continue;

                                    // Find all inbound trips that have matching passengers, prioritize exact matches
                                    const matchingInboundTrips = inboundTrips
                                        .map((inboundTrip) => {
                                            const inboundOffers = offersByTrip.get(inboundTrip.id) || [];
                                            if (inboundOffers.length === 0) return null;

                                            const fulfilledInboundPassengerIds = Array.from(new Set(
                                                inboundOffers.flatMap(offer => getPassengerIdsByStatus(offer, "FULFILLED"))
                                            ));

                                            // Get common fulfilled passengers
                                            const commonPassengerIds = fulfilledOutboundPassengerIds.filter((id: string) =>
                                                fulfilledInboundPassengerIds.includes(id)
                                            );

                                            // Check if any of these passenger-trip combinations are already processed
                                            const unprocessedPassengers = commonPassengerIds.filter((pid: string) => {
                                            const key = getPassengerTripKey(pid, outboundTrip, inboundTrip);
                                                return !processedPassengerTripCombinations.has(key);
                                            });

                                            if (unprocessedPassengers.length === 0) return null;

                                            return {
                                                trip: inboundTrip,
                                                commonPassengerIds: unprocessedPassengers,
                                                matchScore: unprocessedPassengers.length 
                                            };
                                        })
                                        .filter((match): match is { trip: any; commonPassengerIds: string[]; matchScore: number } => match !== null)
                                        .sort((a, b) => b.matchScore - a.matchScore); 

                                    for (const match of matchingInboundTrips) {
                                        const correspondingInboundTrip = match.trip;
                                        const commonPassengerIdsForThisMatch = match.commonPassengerIds;
                                        
                                        if (commonPassengerIdsForThisMatch.length === 0) continue;

                                        const inboundOffers = offersByTrip.get(correspondingInboundTrip.id) || [];
                                        if (inboundOffers.length === 0) continue;

                                        const outboundDate = new Date(outboundTrip.startTime);
                                        const inboundDate = new Date(correspondingInboundTrip.startTime);

                                        // Fix: Use Asia/Dubai (UAE) time for comparison
                                        const nowDubai = bookingsPastPivotDubai();
                                        const outboundDubai = dayjs(outboundDate).tz("Asia/Dubai");
                                        const inboundDubai = dayjs(inboundDate).tz("Asia/Dubai");
                                        const isOutboundPast = outboundDubai.isBefore(nowDubai);
                                        const isInboundPast = inboundDubai.isBefore(nowDubai);

                                        if (isOutboundPast !== isInboundPast) {
                                            continue;
                                        }

                                        
                                        const comprehensivePassengerListForRoundTrip = commonPassengerIdsForThisMatch;

                                        if (comprehensivePassengerListForRoundTrip.length === 0) {
                                            continue;
                                        }

                                        const filteredOutboundOffers = outboundOffers.map(offer => ({
                                            ...offer,
                                            admissions: offer.admissions?.filter((admission: any) =>
                                                admission.status === "FULFILLED" &&
                                                admission.passengerIds?.some((pid: string) =>
                                                    comprehensivePassengerListForRoundTrip.includes(pid)
                                                )
                                            ) || []
                                        })).filter(offer => offer.admissions.length > 0);

                                        const filteredInboundOffers = inboundOffers.map(offer => ({
                                            ...offer,
                                            admissions: offer.admissions?.filter((admission: any) =>
                                                admission.status === "FULFILLED" &&
                                                admission.passengerIds?.some((pid: string) =>
                                                    comprehensivePassengerListForRoundTrip.includes(pid)
                                                )
                                            ) || []
                                        })).filter(offer => offer.admissions.length > 0);

                                        if (filteredOutboundOffers.length === 0 || filteredInboundOffers.length === 0) {
                                            continue;
                                        }

                                        // Use primary offers (non-shuttle preferred) from filtered offers
                                        const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                        const primaryOutboundOffer = pickNonShuttle(filteredOutboundOffers) || filteredOutboundOffers[0];
                                        const primaryInboundOffer = pickNonShuttle(filteredInboundOffers) || filteredInboundOffers[0];

                                        const roundTripBooking = createBookingForPassengers(
                                            outboundTrip,                       // tripDataForOutboundLeg
                                            primaryOutboundOffer,               // offerDataForOutboundLeg
                                            comprehensivePassengerListForRoundTrip, // passengerIdsToIncludeInResolvedList
                                            "FULFILLED",                        // status
                                            { trip: correspondingInboundTrip, offer: primaryInboundOffer } // inboundLegDetails
                                        );
                                        // Collect fulfillment IDs only from filtered outbound offers for these passengers
                                        roundTripBooking.fulfillmentIds = Array.from(new Set(
                                            filteredOutboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                        ));
                                        if (roundTripBooking.inboundData) {
                                            roundTripBooking.inboundData.fulfillmentIds = Array.from(new Set(
                                                filteredInboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                            ));
                                        }
                                        bookings.push(roundTripBooking);

                                        // Mark passenger-trip combinations as processed
                                        comprehensivePassengerListForRoundTrip.forEach((passengerId: string) => {
                                            const key = getPassengerTripKey(passengerId, outboundTrip, correspondingInboundTrip);
                                            processedPassengerTripCombinations.add(key);
                                        });
                                    }
                                }

                                // Process remaining trips that were not part of a round trip using grouped offers
                                for (const [tripId, tripOffers] of offersByTrip) {
                                    const trip = booking.trips.find((t) => t.id === tripId);
                                    if (!trip) continue;

                                    const allNonExchangedPassengerIds = Array.from(new Set(
                                        tripOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                    ));

                                    
                                    const unprocessedPassengerIds = allNonExchangedPassengerIds.filter((passengerId: string) => {
                                        
                                        const tripDirection = trip.direction;
                                        if (tripDirection === TripTypeEnum.OUT_BOUND) {
                                            return !inboundTrips.some((inboundTrip) => {
                                                const key = getPassengerTripKey(passengerId, trip, inboundTrip);
                                                return processedPassengerTripCombinations.has(key);
                                            });
                                        } else if (tripDirection === TripTypeEnum.IN_BOUND) {
                                            return !outboundTrips.some((outboundTrip) => {
                                                const key = getPassengerTripKey(passengerId, outboundTrip, trip);
                                                return processedPassengerTripCombinations.has(key);
                                            });
                                        }
                                        return true;
                                    });

                                    // If all passengers have been processed, skip this trip
                                    if (unprocessedPassengerIds.length === 0) {
                                        continue;
                                    }

                                    // Filter offers to only include non-EXCHANGED admissions for unprocessed passengers
                                    // Exclude EXCHANGED admissions as those passengers have been moved to different trips
                                    const filteredTripOffers = tripOffers.map(offer => ({
                                        ...offer,
                                        admissions: offer.admissions?.filter((admission: any) =>
                                            admission.status !== "EXCHANGED" &&
                                            admission.passengerIds?.some((pid: string) =>
                                                unprocessedPassengerIds.includes(pid)
                                            )
                                        ) || []
                                    })).filter(offer => offer.admissions.length > 0);

                                    if (filteredTripOffers.length === 0) {
                                        continue;
                                    }

                                    // Get all fulfilled passenger IDs from filtered offers
                                    const fulfilledPassengerIds = Array.from(new Set(
                                        filteredTripOffers.flatMap(offer => getPassengerIdsByStatus(offer, "FULFILLED"))
                                    ));

                                    // Check if all admissions are refunded
                                    const allAdmissions = filteredTripOffers.flatMap(o => o.admissions || []);
                                    const allRefunded = allAdmissions.length > 0 && allAdmissions.every((a: any) => a.status === "REFUNDED" || a.status === "RELEASED");

                                    if (allRefunded) {
                                        // Use the first offer as representative for refunded case
                                        const refundedBooking = handleRefundedOffer(trip, filteredTripOffers[0]);
                                        if (refundedBooking) {
                                            bookings.push(refundedBooking);
                                        }
                                        continue;
                                    }

                                    // For FULFILLED one-way trips or partial FULFILLED/REFUNDED
                                    if (fulfilledPassengerIds.length > 0) {
                                        // Use primary offer (non-shuttle preferred) from filtered offers
                                        const pickNonShuttle = (offers: any[]) => offers.find(o => !isShuttleOffer(o));
                                        const primaryOffer = pickNonShuttle(filteredTripOffers) || filteredTripOffers[0];

                                        const booking = createBookingForPassengers(
                                            trip,                           // tripDataForOutboundLeg
                                            primaryOffer,                   // offerDataForOutboundLeg
                                            unprocessedPassengerIds,        // passengerIdsToIncludeInResolvedList (only unprocessed ones)
                                            "FULFILLED"                     // status
                                        );
                                        // Collect fulfillment IDs from filtered offers for these passengers
                                        booking.fulfillmentIds = Array.from(new Set(
                                            filteredTripOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                        ));
                                        bookings.push(booking);
                                    }
                                }

                                return bookings;
                            } catch (error) {
                                console.error("Error in handleSplitRoundTrip:", error);
                                return [];
                            }
                        };

                        if (bookedOffers.length > 0) {
                            const bookings: IBookedTrips[] = [];
                            const processedOffers = new Set<string>(); // To track offers already processed

                            // Filter trips to only include those with non-fully-exchanged offers
                            // A trip is valid if it has at least one offer where not ALL admissions are EXCHANGED
                            // Trips the passenger still holds, oldest first. Screening EXCHANGED
                            // alone kept a change-date's refunded shuttle "valid", so the merge
                            // below drew it — on its ORIGINAL date — as the next ticket. See
                            // tripsStillHeld for why getAllOfferWithoutExchange is not widened.
                            //
                            // A CANCELLED leg's trip is dropped here too, and stays dropped: a row
                            // names what its card ACTS on (change-date probes it, the cancel page
                            // scopes to it), and there is nothing left to act on. Showing what was
                            // cancelled is the details page's job — see cancelledLegTripIds, read
                            // there.
                            const tripsWithValidOffers = tripsStillHeld(booking.trips, bookedOffers);

                            /**
                             * Bucketed by the leg each trip SERVES, not by its stamped
                             * `direction` — the backend stamps every onward shuttle
                             * OUT_BOUND, including the one that runs after the inbound
                             * train lands. A train reports its own direction unchanged,
                             * so a healthy round trip buckets exactly as before.
                             *
                             * It matters once a leg is cancelled. Refund the outbound
                             * leg and `tripsStillHeld` drops its train, leaving the
                             * INBOUND leg's shuttle as the only surviving OUT_BOUND-
                             * stamped trip. `handleSplitRoundTrip` then read
                             * outbound=[shuttle] / inbound=[train] as a round trip —
                             * `pickPrimaryTrip` falls back to `trips[0]` when a
                             * direction holds no train — and the next-ticket block
                             * promoted that shuttle to its own booking card. Booking
                             * UR5SK2 rendered "Abu Dhabi -> Shuttle Corniche" as My
                             * next ticket, and its details page showed no train,
                             * "0 passengers" and a Total of AED 0.
                             *
                             * Matched against the WHOLE booking's trips rather than the
                             * surviving ones, per onwardTripDirection's own contract: a
                             * shuttle whose train has been refunded must still be able
                             * to find it.
                             */
                            const outboundTrips = tripsWithValidOffers.filter(
                                (t) => shuttleServesDirection(t, booking.trips, TripTypeEnum.OUT_BOUND),
                            );
                            const inboundTrips = tripsWithValidOffers.filter(
                                (t) => shuttleServesDirection(t, booking.trips, TripTypeEnum.IN_BOUND),
                            );

                            // Attempt to handle split round trips first if applicable
                            const splitBookings = handleSplitRoundTrip(
                                outboundTrips,
                                inboundTrips,
                                bookedOffers,
                            );
                            if (splitBookings.length > 0) {
                                // Add split bookings
                                splitBookings.forEach((b) => {
                                    bookings.push(b);
                                });

                                // Mark ALL offers as processed to prevent individual processing when split bookings are created
                                bookedOffers.forEach(offer => {
                                    processedOffers.add(offer.offerId);
                                });
                            }

                            // Process remaining bookedOffers not covered by split round trip logic
                            const shouldProcessIndividually = splitBookings.length === 0;
                            if (shouldProcessIndividually) {
                                // Group offers by trip ID to handle connecting trains properly
                                const offersByTrip = new Map<string, any[]>();

                                for (const offer of bookedOffers) {
                                    if (processedOffers.has(offer.offerId)) continue;

                                    const tripId = offer.tripCoverage?.coveredTripId;
                                    if (tripId) {
                                        if (!offersByTrip.has(tripId)) {
                                            offersByTrip.set(tripId, []);
                                        }
                                        offersByTrip.get(tripId)!.push(offer);
                                    }
                                }

                                // Process each trip with its associated offers
                                for (const [tripId, tripOffers] of offersByTrip) {
                                    const trip = booking.trips.find((t) => t.id === tripId);
                                    if (!trip) continue;

                                    // Check if all offers for this trip are refunded
                                    const allOffersRefunded = tripOffers.every(offer =>
                                        areAllAdmissionsSameStatus(offer.admissions, "REFUNDED") ||
                                        areAllAdmissionsSameStatus(offer.admissions, "RELEASED")
                                    );

                                    if (allOffersRefunded) {
                                        const refundedBooking = handleRefundedOffer(trip, tripOffers[0]);
                                        if (refundedBooking) {
                                            bookings.push(refundedBooking);
                                            tripOffers.forEach(offer => processedOffers.add(offer.offerId));
                                        }
                                    } else {
                                        const hasAnyFulfilledPassengers = tripOffers.some(offer =>
                                            getPassengerIdsByStatus(offer, "FULFILLED").length > 0
                                        );

                                        if (hasAnyFulfilledPassengers) {
                                            // Collect all non-exchanged passenger IDs from all offers for this trip
                                            const allNonExchangedPassengerIds = Array.from(new Set(
                                                tripOffers.flatMap(offer => nonExchangedPassengerIds(offer))
                                            ));

                                            // Use the first non-shuttle offer as the primary offer, or first offer if all are shuttle
                                            const primaryOffer = tripOffers.find(offer => !isShuttleOffer(offer)) || tripOffers[0];

                                            if (allNonExchangedPassengerIds.length > 0) {
                                                const booking = createBookingForPassengers(
                                                    trip,                           // tripDataForOutboundLeg
                                                    primaryOffer,                   // offerDataForOutboundLeg (primary offer)
                                                    allNonExchangedPassengerIds,    // passengerIdsToIncludeInResolvedList (all passengers from all offers)
                                                    "FULFILLED"                     // status
                                                );
                                                // Collect fulfillment IDs from all offers for this trip
                                                booking.fulfillmentIds = Array.from(new Set(
                                                    tripOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                                ));
                                                bookings.push(booking);
                                                tripOffers.forEach(offer => processedOffers.add(offer.offerId));
                                            }
                                        }
                                    }
                                }
                            }

                            if (bookings.length > 0) {
                                return bookings;
                            }
                        }

                        // Find outbound and inbound trips by direction for status lookup.
                        // Prefer TRAIN trips: a standalone onward-shuttle trip is tagged
                        // OUT_BOUND and can sort first, so a raw find(OUT_BOUND) would pick
                        // the shuttle and drive status/offer/card rendering off it.
                        const trainTripsForStatus = preferTrainTrips(booking.trips);
                        const outboundTripForStatus = trainTripsForStatus.find((t: any) => t.direction === TripTypeEnum.OUT_BOUND) || trainTripsForStatus[0];
                        const inboundTripForStatus = trainTripsForStatus.find((t: any) => t.direction === TripTypeEnum.IN_BOUND) || trainTripsForStatus[1];

                        const outboundOfferForStatus = bookedOffers.find(
                            (o) =>
                                o.tripCoverage?.coveredTripId === outboundTripForStatus?.id &&
                                o.admissions?.some((admission: any) => admission.status === "FULFILLED"),
                        ) || bookedOffers.find(
                            (o) => o.tripCoverage?.coveredTripId === outboundTripForStatus?.id,
                        );
                        const inboundOfferForStatus = bookedOffers.find(
                            (o) =>
                                o.tripCoverage?.coveredTripId === inboundTripForStatus?.id &&
                                o.admissions?.some((admission: any) => admission.status === "FULFILLED"),
                        ) || bookedOffers.find(
                            (o) => o.tripCoverage?.coveredTripId === inboundTripForStatus?.id,
                        );

                        const outboundAdmissions = outboundOfferForStatus?.admissions || [];
                        const inboundAdmissions = inboundOfferForStatus?.admissions || [];

                        const outboundStatus = resolveOfferStatus(outboundAdmissions, bookingTrip.status || "UNKNOWN");

                        const inboundStatus = resolveOfferStatus(inboundAdmissions, "UNKNOWN");

                        // Handle round trips with one leg refunded or exchanged based on date/activeTab.
                        // Gate on the TRAIN-trip count: a one-way train booking with an onward
                        // shuttle has 2 raw trips but only 1 train leg — it must NOT be treated
                        // as a round trip (that path assumes a real inbound trip exists).
                        if (trainTripsForStatus.length > 1) {
                            // Find outbound and inbound trips by direction (not by array index),
                            // preferring TRAIN trips so an OUT_BOUND-tagged shuttle can't take the slot.
                            const outboundTrip = trainTripsForStatus.find((t: any) => t.direction === TripTypeEnum.OUT_BOUND) || trainTripsForStatus[0];
                            const inboundTrip = trainTripsForStatus.find((t: any) => t.direction === TripTypeEnum.IN_BOUND) || trainTripsForStatus[1];
                            const allOutboundOffers = bookedOffers?.filter(
                                (o) => o.tripCoverage?.coveredTripId === outboundTrip.id
                            );
                            const allInboundOffers = bookedOffers?.filter(
                                (o) => o.tripCoverage?.coveredTripId === inboundTrip.id
                            );
                            const pickPrimaryOfferForTrip = (offers: any[] = [], fallbackOffer: any = undefined) => {
                                const offersWithFulfilled = offers.filter((offer) =>
                                    offer?.admissions?.some((admission: any) => admission.status === "FULFILLED")
                                );
                                const candidateOffers = offersWithFulfilled.length > 0 ? offersWithFulfilled : offers;
                                const nonShuttleOffer = candidateOffers.find((offer) => !isShuttleOffer(offer));
                                return nonShuttleOffer || candidateOffers[0] || fallbackOffer;
                            };
                            const outboundOfferForTrip = pickPrimaryOfferForTrip(allOutboundOffers, bookedOffers[0]);
                            const inboundOfferForTrip = pickPrimaryOfferForTrip(allInboundOffers, bookedOffers[1] || bookedOffers[0]);
                            const outboundAdmissionForDisplay =
                                outboundOfferForTrip?.admissions?.find((admission: any) => admission.status === "FULFILLED") ||
                                outboundOfferForTrip?.admissions?.[0];
                            const inboundAdmissionForDisplay =
                                inboundOfferForTrip?.admissions?.find((admission: any) => admission.status === "FULFILLED") ||
                                inboundOfferForTrip?.admissions?.[0];

                            const outboundDate = new Date(outboundTrip.startTime);
                            const inboundDate = new Date(inboundTrip.startTime);
                            const currentDate = new Date();
                            currentDate.setUTCHours(currentDate.getUTCHours() + 4);

                            // Get activeTab from the params
                            const activeTab = this.params?.activeTab || "1";

                            // Check if this booking contains shuttle services - if so, don't split based on date/time
                            const hasShuttle = hasShuttleServices(bookedOffers);

                            // Only split if one leg is in past and one is in future AND it's not a shuttle booking
                            // const isOutboundPast = outboundDate < currentDate;
                            // const isInboundPast = inboundDate < currentDate;
                            // Fix: Use Asia/Dubai (UAE) time for comparison
                            const nowDubai = bookingsPastPivotDubai();
                            const outboundDubai = dayjs(outboundDate).tz("Asia/Dubai");
                            const inboundDubai = dayjs(inboundDate).tz("Asia/Dubai");
                            const isOutboundPast = outboundDubai.isBefore(nowDubai);
                            const isInboundPast = inboundDubai.isBefore(nowDubai);

                            // Scenario: Full round trip, or two distinct one-way trips (future and past)
                            // For shuttle bookings, always keep as round trip regardless of date/time
                            if (
                                (hasShuttle || (activeTab === "1" && !isOutboundPast && !isInboundPast)) &&
                                outboundStatus === "FULFILLED" &&
                                inboundStatus === "FULFILLED"
                            ) {
                                // Both are in future and fulfilled, return as single round trip
                                const hasOBForPair = (outboundTrip?.direction === TripTypeEnum.OUT_BOUND) || (inboundTrip?.direction === TripTypeEnum.OUT_BOUND);
                                const hasIBForPair = (outboundTrip?.direction === TripTypeEnum.IN_BOUND) || (inboundTrip?.direction === TripTypeEnum.IN_BOUND);
                                const computedBookingType = hasOBForPair && hasIBForPair ? "roundTrip" : "oneWayTrip";
                                return [
                                    {
                                        ...bookingTrip,
                                        bookingId: bookingTrip.id,
                                        fromName: stopManager.getStopNameById(
                                            outboundTrip.origin.stopPlaceRef
                                        ),
                                        toName: stopManager.getStopNameById(
                                            outboundTrip.destination.stopPlaceRef
                                        ),
                                        from: outboundTrip.origin.stopPlaceRef,
                                        to: outboundTrip.destination.stopPlaceRef,
                                        departureTime: convertTimeToArabic(outboundTrip.startTime),
                                        arrivalTime: convertTimeToArabic(outboundTrip.endTime),
                                        class:
                                            outboundAdmissionForDisplay?.summary?.split(" ")[0] ||
                                            "",
                                        summary: outboundAdmissionForDisplay?.summary || "",
                                        isOutboundIsDirectTrain:
                                            !outboundAdmissionForDisplay?.summary?.toLowerCase().includes(trainType.shuttle),
                                        duration: convertISO8601ToDuration(outboundTrip.duration),
                                        trainNumber: booking.bookingCode,
                                        stops:
                                            outboundTrip.transfers > 0
                                                ? Array(outboundTrip.transfers).fill("Stop")
                                                : [],
                                        status: "FULFILLED",
                                        startDate: outboundTrip.startTime
                                            ? new Date(outboundTrip.startTime).toLocaleDateString(
                                                "en-US",
                                                {
                                                    weekday: "short",
                                                    day: "2-digit",
                                                    month: "short",
                                                },
                                            )
                                            : "",
                                        startDateFormat: outboundTrip.startTime
                                            ? getShortDateFromDateStringFormatted(outboundTrip.startTime)
                                            : "",
                                        arrivalDateTime: outboundTrip.endTime,
                                        departureDateTime: outboundTrip.startTime,
                                        bookingType: computedBookingType,
                                        outBoundOfferID: outboundOfferForTrip?.offerId ?? null,
                                        confirmedPrice: calculatePrice(booking.confirmedPrice),
                                        provisionalPrice: calculatePrice(booking.provisionalPrice),
                                        confirmationTimeLimit: booking.confirmationTimeLimit,
                                        outboundConfirmedPrice: outboundConfirmedPrice,
                                        inboundConfirmedPrice: inboundConfirmedPrice,
                                        inboundOfferID: inboundOfferForTrip?.offerId ?? null,
                                        selectedPriceClass: getTrainClassTypeBaseOnProduct(outboundOfferForTrip?.products || []) || "",
                                        productName: getProductNameFromOffer(outboundOfferForTrip),
                                        inboundData: computedBookingType === "roundTrip" ? {
                                            fromName: stopManager.getStopNameById(
                                                inboundTrip.origin.stopPlaceRef,
                                            ),
                                            toName: stopManager.getStopNameById(
                                                inboundTrip.destination.stopPlaceRef,
                                            ),
                                            from: inboundTrip.origin.stopPlaceRef,
                                            to: inboundTrip.destination.stopPlaceRef,
                                            duration: convertISO8601ToDuration(inboundTrip.duration),
                                            stops:
                                                inboundTrip.transfers > 0
                                                    ? Array(inboundTrip.transfers).fill("Stop")
                                                    : [],
                                            departureTime: convertTimeToArabic(inboundTrip.startTime),
                                            arrivalTime: convertTimeToArabic(inboundTrip.endTime),
                                            arrivalDateTime: inboundTrip.endTime,
                                            departureDateTime: inboundTrip.startTime,
                                            status: "FULFILLED",
                                            inboundConfirmedPrice: inboundConfirmedPrice,
                                            summary: inboundAdmissionForDisplay?.summary || "",
                                            selectedPriceClass: getTrainClassTypeBaseOnProduct(inboundOfferForTrip?.products || []) || "",
                                            productName: getProductNameFromOffer(inboundOfferForTrip),
                                            fulfillmentIds: Array.from(new Set(
                                                allInboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                            )),
                                        } : undefined,
                                        repeatBooking: true,
                                        noModificationOnBooking: noModificationOnBooking,
                                        passengers: booking.passengers.map(toCardPassenger),
                                        isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                        fulfillmentIds: getFulfillmentAdmissionIdsFromOffer(outboundOfferForTrip),
                                        isBookingHasShuttle: isBookingHasShuttle,
                                    isShuttleOnlyBooking: isShuttleOnlyBooking
                                    },
                                ];
                            } else if ((hasShuttle && activeTab === "2") || (activeTab === "2" && isOutboundPast && isInboundPast)) {
                                // Both are in past, return as single round trip
                                const hasOBForPastPair = (outboundTrip?.direction === TripTypeEnum.OUT_BOUND) || (inboundTrip?.direction === TripTypeEnum.OUT_BOUND);
                                const hasIBForPastPair = (outboundTrip?.direction === TripTypeEnum.IN_BOUND) || (inboundTrip?.direction === TripTypeEnum.IN_BOUND);
                                const computedBookingTypePast = hasOBForPastPair && hasIBForPastPair ? "roundTrip" : "oneWayTrip";
                                return [
                                    {
                                        ...bookingTrip,
                                        bookingId: bookingTrip.id,
                                        fromName: stopManager.getStopNameById(
                                            outboundTrip.origin.stopPlaceRef,
                                        ),
                                        toName: stopManager.getStopNameById(
                                            outboundTrip.destination.stopPlaceRef,
                                        ),
                                        from: outboundTrip.origin.stopPlaceRef,
                                        to: outboundTrip.destination.stopPlaceRef,
                                        departureTime: convertTimeToArabic(outboundTrip.startTime),
                                        arrivalTime: convertTimeToArabic(outboundTrip.endTime),
                                        class:
                                            outboundAdmissionForDisplay?.summary?.split(" ")[0] ||
                                            "",
                                        summary: outboundAdmissionForDisplay?.summary || "",
                                        isOutboundIsDirectTrain:
                                            !outboundAdmissionForDisplay?.summary?.toLowerCase().includes("shuttle"),
                                        duration: convertISO8601ToDuration(outboundTrip.duration),
                                        trainNumber: booking.bookingCode,
                                        stops:
                                            outboundTrip.transfers > 0
                                                ? Array(outboundTrip.transfers).fill("Stop")
                                                : [],
                                        status: "FULFILLED",
                                        startDate: outboundTrip.startTime
                                            ? new Date(outboundTrip.startTime).toLocaleDateString(
                                                "en-US",
                                                {
                                                    weekday: "short",
                                                    day: "2-digit",
                                                    month: "short",
                                                },
                                            )
                                            : "",
                                        startDateFormat: outboundTrip.startTime
                                            ? getShortDateFromDateStringFormatted(outboundTrip.startTime)
                                            : "",
                                        arrivalDateTime: outboundTrip.endTime,
                                        departureDateTime: outboundTrip.startTime,
                                        bookingType: computedBookingTypePast,
                                        outBoundOfferID: outboundOfferForTrip?.offerId ?? null,
                                        confirmedPrice: calculatePrice(booking.confirmedPrice),
                                        provisionalPrice: calculatePrice(booking.provisionalPrice),
                                        confirmationTimeLimit: booking.confirmationTimeLimit,
                                        outboundConfirmedPrice: outboundConfirmedPrice,
                                        inboundConfirmedPrice: inboundConfirmedPrice,
                                        inboundOfferID: inboundOfferForTrip?.offerId ?? null,
                                        selectedPriceClass: getTrainClassTypeBaseOnProduct(outboundOfferForTrip?.products || []) || "",
                                        productName: getProductNameFromOffer(outboundOfferForTrip),
                                        inboundData: computedBookingTypePast === "roundTrip" ? {
                                            fromName: stopManager.getStopNameById(
                                                inboundTrip.origin.stopPlaceRef,
                                            ),
                                            toName: stopManager.getStopNameById(
                                                inboundTrip.destination.stopPlaceRef,
                                            ),
                                            from: inboundTrip.origin.stopPlaceRef,
                                            to: inboundTrip.destination.stopPlaceRef,
                                            duration: convertISO8601ToDuration(inboundTrip.duration),
                                            stops:
                                                inboundTrip.transfers > 0
                                                    ? Array(inboundTrip.transfers).fill("Stop")
                                                    : [],
                                            departureTime: convertTimeToArabic(inboundTrip.startTime),
                                            arrivalTime: convertTimeToArabic(inboundTrip.endTime),
                                            arrivalDateTime: inboundTrip.endTime,
                                            departureDateTime: inboundTrip.startTime,
                                            status: "FULFILLED",
                                            inboundConfirmedPrice: inboundConfirmedPrice,
                                            summary: inboundAdmissionForDisplay?.summary || "",
                                            isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                            selectedPriceClass: getTrainClassTypeBaseOnProduct(inboundOfferForTrip?.products || []) || "",
                                            productName: getProductNameFromOffer(inboundOfferForTrip),
                                            fulfillmentIds: Array.from(new Set(
                                                allInboundOffers.flatMap(offer => getFulfillmentAdmissionIdsFromOffer(offer))
                                            )),
                                        } : undefined,
                                        repeatBooking: true,
                                        passengers: booking.passengers.map(toCardPassenger),
                                        isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                        fulfillmentIds: getFulfillmentAdmissionIdsFromOffer(outboundOfferForTrip),
                                        noModificationOnBooking: noModificationOnBooking,
                                        isBookingHasShuttle: isBookingHasShuttle,
                                    isShuttleOnlyBooking: isShuttleOnlyBooking
                                    },
                                ];
                            }

                            const individualTrips: IBookedTrips[] = [];

                            // Handle outbound leg if it's FULFILLED and not in past tab
                            // For shuttle bookings, don't split into individual trips
                            if (
                                !hasShuttle &&
                                outboundStatus === "FULFILLED" &&
                                (activeTab === "1" ? !isOutboundPast : isOutboundPast)
                            ) {
                                individualTrips.push({
                                    ...bookingTrip,
                                    bookingId: bookingTrip.id,
                                    fromName: stopManager.getStopNameById(
                                        outboundTrip.origin.stopPlaceRef,
                                    ),
                                    toName: stopManager.getStopNameById(
                                        outboundTrip.destination.stopPlaceRef,
                                    ),
                                    from: outboundTrip.origin.stopPlaceRef,
                                    to: outboundTrip.destination.stopPlaceRef,
                                    departureTime: convertTimeToArabic(outboundTrip.startTime),
                                    arrivalTime: convertTimeToArabic(outboundTrip.endTime),
                                    class:
                                        outboundAdmissionForDisplay?.summary?.split(" ")[0] ||
                                        "",
                                    summary: outboundAdmissionForDisplay?.summary || "",
                                    isOutboundIsDirectTrain:
                                        !outboundAdmissionForDisplay?.summary?.toLowerCase().includes("shuttle"),
                                    duration: convertISO8601ToDuration(outboundTrip.duration),
                                    trainNumber: booking.bookingCode,
                                    stops:
                                        outboundTrip.transfers > 0
                                            ? Array(outboundTrip.transfers).fill("Stop")
                                            : [],
                                    status: "FULFILLED",
                                    startDate: outboundTrip.startTime
                                        ? new Date(outboundTrip.startTime).toLocaleDateString(
                                            "en-US",
                                            {
                                                weekday: "short",
                                                day: "2-digit",
                                                month: "short",
                                            },
                                        )
                                        : "",
                                    startDateFormat: outboundTrip.startTime
                                        ? getShortDateFromDateStringFormatted(outboundTrip.startTime)
                                        : "",
                                    arrivalDateTime: outboundTrip.endTime,
                                    departureDateTime: outboundTrip.startTime,
                                    bookingType: "oneWayTrip",
                                    outBoundOfferID: outboundOfferForTrip?.offerId ?? null,
                                    confirmedPrice: calculatePrice(booking.confirmedPrice),
                                    provisionalPrice: calculatePrice(booking.provisionalPrice),
                                    confirmationTimeLimit: booking.confirmationTimeLimit,
                                    outboundConfirmedPrice: outboundConfirmedPrice,
                                    inboundConfirmedPrice: inboundConfirmedPrice,
                                    inboundOfferID: undefined,
                                    inboundData: undefined,
                                    repeatBooking: true,
                                    direction: TripTypeEnum.OUT_BOUND,
                                    passengers: booking.passengers.map(toCardPassenger),
                                    isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                    // Split outbound entry: only the outbound exchange-slot governs eligibility here.
                                    noModificationOnBooking: noModOutbound,
                                    selectedPriceClass: getTrainClassTypeBaseOnProduct(outboundOfferForTrip?.products || []) || "",
                                    productName: getProductNameFromOffer(outboundOfferForTrip),
                                    fulfillmentIds: getFulfillmentAdmissionIdsFromOffer(outboundOfferForTrip),
                                    isBookingHasShuttle: isBookingHasShuttle,
                                    isShuttleOnlyBooking: isShuttleOnlyBooking
                                });
                            }

                            // Handle inbound leg if it's FULFILLED and not in past tab
                            // For shuttle bookings, don't split into individual trips
                            if (
                                !hasShuttle &&
                                inboundStatus === "FULFILLED" &&
                                (activeTab === "1" ? !isInboundPast : isInboundPast)
                            ) {
                                individualTrips.push({
                                    ...bookingTrip,
                                    bookingId: bookingTrip.id,
                                    fromName: stopManager.getStopNameById(
                                        inboundTrip.origin.stopPlaceRef,
                                    ),
                                    toName: stopManager.getStopNameById(
                                        inboundTrip.destination.stopPlaceRef,
                                    ),
                                    from: inboundTrip.origin.stopPlaceRef,
                                    to: inboundTrip.destination.stopPlaceRef,
                                    departureTime: convertTimeToArabic(inboundTrip.startTime),
                                    arrivalTime: convertTimeToArabic(inboundTrip.endTime),
                                    class:
                                        inboundAdmissionForDisplay?.summary?.split(" ")[0] ||
                                        "",
                                    summary: inboundAdmissionForDisplay?.summary || "",
                                    isOutboundIsDirectTrain:
                                        !inboundAdmissionForDisplay?.summary?.toLowerCase().includes("shuttle"),
                                    duration: convertISO8601ToDuration(inboundTrip.duration),
                                    trainNumber: booking.bookingCode,
                                    stops:
                                        inboundTrip.transfers > 0
                                            ? Array(inboundTrip.transfers).fill("Stop")
                                            : [],
                                    status: "FULFILLED",
                                    startDate: inboundTrip.startTime
                                        ? new Date(inboundTrip.startTime).toLocaleDateString(
                                            "en-US",
                                            {
                                                weekday: "short",
                                                day: "2-digit",
                                                month: "short",
                                            },
                                        )
                                        : "",
                                    startDateFormat: inboundTrip.startTime
                                        ? getShortDateFromDateStringFormatted(inboundTrip.startTime)
                                        : "",
                                    arrivalDateTime: inboundTrip.endTime,
                                    departureDateTime: inboundTrip.startTime,
                                    bookingType: "oneWayTrip",
                                    outBoundOfferID: inboundOfferForTrip?.offerId ?? null,
                                    confirmedPrice: calculatePrice(booking.confirmedPrice),
                                    provisionalPrice: calculatePrice(booking.provisionalPrice),
                                    confirmationTimeLimit: booking.confirmationTimeLimit,
                                    inboundConfirmedPrice: inboundConfirmedPrice,
                                    inboundOfferID: undefined,
                                    inboundData: undefined,
                                    repeatBooking: true,
                                    direction: TripTypeEnum.IN_BOUND,
                                    passengers: booking.passengers.map(toCardPassenger),
                                    isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                    // Split inbound entry: only the inbound exchange-slot governs eligibility here.
                                    noModificationOnBooking: noModInbound,
                                    selectedPriceClass: getTrainClassTypeBaseOnProduct(inboundOfferForTrip?.products || []) || "",
                                    productName: getProductNameFromOffer(inboundOfferForTrip),
                                    fulfillmentIds: getFulfillmentAdmissionIdsFromOffer(inboundOfferForTrip),
                                    isBookingHasShuttle: isBookingHasShuttle,
                                    isShuttleOnlyBooking: isShuttleOnlyBooking
                                });
                            }
                            return individualTrips;
                        }

                        // Update booking trip status based on offer status if not handled by split logic
                        bookingTrip.status = outboundStatus;
                        bookingTrip.noModificationOnBooking = noModificationOnBooking;

                        if (
                            allBookedOffersWithoutExchange.length > 0 &&
                            allTripWithoutExchange.length > 0
                        ) {
                            const updatedPassenger: ISelectedPassenger[] =
                                booking.passengers.map((pass) => {
                                    const selectedPass: ISelectedPassenger = {
                                        email: pass?.detail?.email || "",
                                        firstName: pass?.detail?.firstName || "",
                                        id: pass.id,
                                        lastName: pass?.detail?.lastName || "",
                                        mobile: "",
                                        remember: pass.remember || false,
                                        age: pass.age,
                                        seatingDetails: null,
                                        cards: pass.cards || [],
                                        prmNeeds: pass.prmNeeds || [],
                                    };
                                    return selectedPass;
                                });
                            const admissionsParams: IGetPassengerListByOfferAdmissionsActionParams =
                            {
                                allTrip: allTripWithoutExchange,
                                bookedOffers: allBookedOffersWithoutExchange,
                                passengers: updatedPassenger,
                            };
                            await getPassengerListByOfferAdmissionsAction.setParams(
                                admissionsParams,
                            );
                            await getPassengerListByOfferAdmissionsAction.execute();
                            if (isActionRunSuccessfully(getPassengerListByOfferAdmissionsAction)) {
                                const allPassenger: IBookingPassenger =
                                    getPassengerListByOfferAdmissionsAction.result!;
                                const convertBookingPassenger: IPassenger[] = [];

                                // Process all passengers regardless of status
                                allPassenger.outbound.forEach((p) => {
                                    const bookingPassenger = booking.passengers.find(
                                        (bp) => bp.id === p.id,
                                    );
                                    if (bookingPassenger) {
                                        const passenger: IPassenger = {
                                            id: p.id,
                                            externalRef: bookingPassenger?.externalRef || "",
                                            type: "PERSON",
                                            cards: [],
                                            detail: {
                                                firstName: p.firstName || "",
                                                lastName: p.lastName || "",
                                                email: p.email || "",
                                                age: bookingPassenger?.age,
                                            },
                                            prmNeeds: [],
                                            remember: false,
                                            age: p?.age,
                                            dateOfBirth: bookingPassenger?.dateOfBirth,
                                            seat: "",
                                            coachNumber: "",
                                            placeNumber: "",
                                        };
                                        convertBookingPassenger.push(passenger);
                                    }
                                });

                                // Update booking trip with station names
                                const nonShuttleTripsCovered = new Set<string>();
                                (allBookedOffersWithoutExchange || []).forEach((offer: any) => {
                                    const coveredTripId = offer?.tripCoverage?.coveredTripId;
                                    if (!isShuttleOffer(offer) && coveredTripId) {
                                        nonShuttleTripsCovered.add(coveredTripId);
                                    }
                                });
                                const coveredDirections = new Set<string>();
                                (booking.trips || []).forEach((t: any) => {
                                    if (nonShuttleTripsCovered.has(t.id)) {
                                        coveredDirections.add(t.direction);
                                    }
                                });
                                const hasOutbound = coveredDirections.has(TripTypeEnum.OUT_BOUND);
                                const hasInbound = coveredDirections.has(TripTypeEnum.IN_BOUND);
                                bookingTrip.bookingType = hasOutbound && hasInbound ? "roundTrip" : "oneWayTrip";
                                bookingTrip.fromName = stopManager.getStopNameById(
                                    allTripWithoutExchange[0].origin.stopPlaceRef,
                                );
                                bookingTrip.toName = stopManager.getStopNameById(
                                    allTripWithoutExchange[0].destination.stopPlaceRef,
                                );
                                bookingTrip.trainNumber = booking.bookingCode;
                                bookingTrip.bookingId = bookingTrip.id;
                                bookingTrip.departureTime = convertTimeToArabic(
                                    allTripWithoutExchange[0].startTime,
                                );
                                bookingTrip.arrivalTime = convertTimeToArabic(
                                    allTripWithoutExchange[0].endTime,
                                );
                                bookingTrip.duration = convertISO8601ToDuration(
                                    allTripWithoutExchange[0].duration,
                                );
                                bookingTrip.stops =
                                    allTripWithoutExchange[0].transfers > 0
                                        ? Array(allTripWithoutExchange[0].transfers).fill("Stop")
                                        : [];
                                bookingTrip.startDate = allTripWithoutExchange[0].startTime
                                    ? new Date(allTripWithoutExchange[0].startTime).toLocaleDateString(
                                        "en-US",
                                        {
                                            weekday: "short",
                                            day: "2-digit",
                                            month: "short",
                                        },
                                    )
                                    : "";
                                bookingTrip.startDateFormat = allTripWithoutExchange[0].startTime
                                    ? getShortDateFromDateStringFormatted(
                                        allTripWithoutExchange[0].startTime,
                                    )
                                    : "";
                                bookingTrip.arrivalDateTime =
                                    allTripWithoutExchange[0].endTime;
                                bookingTrip.departureDateTime =
                                    allTripWithoutExchange[0].startTime;
                                bookingTrip.confirmedPrice = calculatePrice(
                                    bookingTrip.confirmedPrice,
                                );
                                bookingTrip.provisionalPrice = calculatePrice(booking.provisionalPrice);
                                bookingTrip.confirmationTimeLimit = booking.confirmationTimeLimit;
                                bookingTrip.outboundConfirmedPrice = outboundConfirmedPrice;
                                bookingTrip.inboundConfirmedPrice = inboundConfirmedPrice;
                                bookingTrip.outBoundOfferID =
                                    allBookedOffersWithoutExchange[0]?.offerId ?? null;
                                bookingTrip.passengers = convertBookingPassenger;
                                bookingTrip.isRetrieveBooking = bookingTrip?.isRetrieveBooking || false;
                                bookingTrip.noModificationOnBooking = noModificationOnBooking
                                /**
                                 * Name this card after the train, not after whichever offer the
                                 * backend happened to put first — the same pick every other
                                 * branch makes. `outBoundOfferID` above deliberately keeps
                                 * `[0]`: it is the row's identity, compared in
                                 * UpcomingBooking.tsx:118 and used as the dedupe key at :2335.
                                 * Moving it with the label would silently change that key.
                                 *
                                 * This branch also never sets `isShuttleOnlyBooking`, so
                                 * BookingData falls back to testing `summary` for "shuttle"
                                 * (BookingData.tsx:456). Taking `summary` from the train's
                                 * admission is what keeps a train booking off the reduced
                                 * shuttle menu here.
                                 */
                                const primaryOfferForCard =
                                    allBookedOffersWithoutExchange.find((offer: any) => !isShuttleOffer(offer)) ||
                                    allBookedOffersWithoutExchange[0];
                                bookingTrip.summary =
                                    primaryOfferForCard?.admissions?.[0]?.summary ||
                                    "";
                                bookingTrip.selectedPriceClass = getTrainClassTypeBaseOnProduct(primaryOfferForCard?.products || []) || "";
                                bookingTrip.productName = getProductNameFromOffer(primaryOfferForCard);
                                // Handle inbound data if it exists
                                if (bookingTrip.inboundData && allTripWithoutExchange.length > 1) {
                                    bookingTrip.inboundData = {
                                        ...bookingTrip.inboundData || {},
                                        fromName: stopManager.getStopNameById(
                                            allTripWithoutExchange[1].origin.stopPlaceRef,
                                        ),
                                        toName: stopManager.getStopNameById(
                                            allTripWithoutExchange[1].destination.stopPlaceRef,
                                        ),
                                        from: allTripWithoutExchange[1].origin.stopPlaceRef,
                                        to: allTripWithoutExchange[1].destination.stopPlaceRef,
                                        duration: allTripWithoutExchange[1]?.duration
                                            ? convertISO8601ToDuration(
                                                allTripWithoutExchange[1].duration,
                                            )
                                            : "",
                                        stops:
                                            allTripWithoutExchange[1]?.transfers > 0
                                                ? Array(allTripWithoutExchange[1]?.transfers).fill("Stop")
                                                : [],
                                        departureTime: convertTimeToArabic(
                                            allTripWithoutExchange[1]?.startTime,
                                        ),
                                        arrivalTime: convertTimeToArabic(
                                            allTripWithoutExchange[1]?.endTime,
                                        ),
                                        arrivalDateTime: allTripWithoutExchange[1]?.endTime,
                                        departureDateTime:
                                            allTripWithoutExchange[1]?.startTime,
                                        status: inboundStatus,
                                        inboundConfirmedPrice: inboundConfirmedPrice,
                                        // `?.` on the index purely to match its siblings below,
                                        // which already guard `[1]`; this line was the lone
                                        // outlier. The guard above cannot actually leave `[1]`
                                        // undefined — `allTripWithoutExchange` is filtered from
                                        // the offers' own `coveredTripId`s, so trips never
                                        // outnumber offers. Cheap consistency, not a live crash:
                                        // this block sits outside the region's try/catch, so a
                                        // throw here would empty the whole bookings list.
                                        summary:
                                            allBookedOffersWithoutExchange[1]?.admissions?.[0]
                                                ?.summary,
                                        isRetrieveBooking: bookingTrip?.isRetrieveBooking || false,
                                        selectedPriceClass: getTrainClassTypeBaseOnProduct(allBookedOffersWithoutExchange[1]?.products || []) || "",
                                        productName: getProductNameFromOffer(allBookedOffersWithoutExchange[1]),
                                    };
                                }
                            }
                        }
                    }
                    return bookingTrip;
                }),
                );

                // Flatten the array of trips since some bookings might return arrays
                const flattenedBookedTrips = updateBookedTrips.flat();

                // Sort the trips by departure date in descending order (newest first)
                const sortedBookedTrips = flattenedBookedTrips.sort((a, b) => {
                    // Get the correct departure date for each trip
                    const getDepartureDate = (trip: any) => {
                        // For inbound trips, use inboundData departure date
                        if (trip.direction === TripTypeEnum.IN_BOUND && trip.inboundData) {
                            return new Date(trip.inboundData.departureDateTime).getTime();
                        }
                        // For all other trips, use the main departure date
                        return new Date(trip.departureDateTime).getTime();
                    };

                    const dateA = getDepartureDate(a);
                    const dateB = getDepartureDate(b);

                    // Sort by most recent first
                    return dateB - dateA;
                });

                // In append mode (lazy "Load more" for Past) we must NOT publish
                // here — the append-merge below concatenates this page onto the
                // already-shown list. Publishing the page-only set here would
                // clobber the accumulated list (the merge then reads it back).
                const appendMode = !!this.params?.append;
                if (!appendMode) {
                    // Update booked trips
                    updateRecoilState("bookedTripsAtom", withShuttleFlag(collapseBookingRows(sortedBookedTrips)));
                }

                // Find next train for quick exchange
                const currentDateTime = new Date();
                currentDateTime.setUTCHours(currentDateTime.getUTCHours() + 4);
                const currentDate = toUTC4(currentDateTime.toISOString());

                // Tab membership pivots one appearance period back, so a train that
                // has just departed stays under Upcoming instead of dropping into
                // Past while its passengers are still on board. Same instant the two
                // search ranges use, or a booking lands in both tabs or in neither.
                const tabPivot = bookingsPastPivot(currentDateTime);

                // Get activeTab from the params
                const activeTab = this.params?.activeTab || "1";

                let filteredTrips: IBookedTrips[] = []
                // if (isAnonymous) {
                //     // filteredTrips = sortedBookedTrips
                //     // Filter trips based on activeTab
                //     filteredTrips = sortedBookedTrips.filter((d) => {
                //         const tripDateTime = new Date(d.departureDateTime);
                //         tripDateTime.setUTCHours(tripDateTime.getUTCHours() + 4);
                //         if (activeTab === "1") {
                //             // Show upcoming trips
                //             return tripDateTime.toISOString() > currentDateTime.toISOString();
                //         } else {
                //             // Show past trips
                //             return tripDateTime.toISOString() <= currentDateTime.toISOString();
                //         }
                //     });
                // } else {
                // Filter trips based on activeTab
                const tabPivotIso = tabPivot.toISOString();
                filteredTrips = sortedBookedTrips.filter((d) => {
                    const tripDateTime = new Date(d.departureDateTime);
                    tripDateTime.setUTCHours(tripDateTime.getUTCHours() + 4);
                    return isDepartureInTab(tripDateTime.toISOString(), activeTab, tabPivotIso);
                });
                // }

                // Sort filtered trips
                filteredTrips.sort((a, b) => {
                    // First sort by status (FULFILLED first)
                    if (a.status === "FULFILLED" && b.status !== "FULFILLED") return -1;
                    if (a.status !== "FULFILLED" && b.status === "FULFILLED") return 1;

                    // Get the correct departure date for each trip
                    const getDepartureDate = (trip: any) => {
                        // For inbound trips, use inboundData departure date
                        if (trip.direction === TripTypeEnum.IN_BOUND && trip.inboundData) {
                            return new Date(trip.inboundData.departureDateTime).getTime();
                        }
                        // For all other trips, use the main departure date
                        return new Date(trip.departureDateTime).getTime();
                    };

                    const dateA = getDepartureDate(a);
                    const dateB = getDepartureDate(b);

                    // Sort based on activeTab
                    if (activeTab === "1") {
                        // For upcoming trips, sort by earliest first
                        return dateA - dateB;
                    } else {
                        // For past trips, sort by most recent first
                        return dateB - dateA;
                    }
                });

                // ACPT-037: one card per booking reference. Folded HERE — after
                // the Upcoming/Past split — so a round trip whose outbound has
                // flown and whose inbound has not still appears in both tabs.
                // Everything downstream (next-ticket pick, the round-trip split
                // below) reads the folded rows, so the list and the next ticket
                // cannot disagree about what a booking looks like.
                filteredTrips = collapseBookingRows(filteredTrips);

                // Update booked trips based on activeTab. In append mode (Load
                // more) concatenate onto the existing list while keeping the
                // previously-computed nearest record intact.
                //
                // Reading bookedTripsAtom here returns the ACCUMULATED list: the
                // page-only publish above is skipped in append mode, and
                // updateRecoilState dispatches on a microtask, so the prior
                // click's write has already flushed (its awaited getBooking calls
                // yield to the microtask queue) while the isLoadingMore guard
                // serializes clicks — one read + one write per run, no race.
                if (appendMode) {
                    const existing = (getRecoilStateValue<IBookedTrips[]>("bookedTripsAtom") as IBookedTrips[] | undefined) || [];
                    const seen = new Set((existing || []).map((b: IBookedTrips) => `${b.bookingId}|${b.outBoundOfferID || ''}|${b.departureDateTime}`));
                    const dedupedNew = filteredTrips.filter((b) => !seen.has(`${b.bookingId}|${b.outBoundOfferID || ''}|${b.departureDateTime}`));
                    // Only bookings not already on screen. Every card is built from the
                    // FULL booking (GET /bookings/{id}), so a reference already listed is
                    // complete; its rows coming back are duplicates, and folding them in
                    // again undid the split the list was built with (the next ticket's
                    // rows were folded with `sameRidersOnly`). Pagination folds summaries
                    // per reference before paging, so this is a guard, not a hot path.
                    // `filteredTrips` is already folded above, so nothing is re-folded.
                    const shownBookingIds = new Set(existing.map((b: IBookedTrips) => b.bookingId).filter(Boolean));
                    const newBookings = dedupedNew.filter((b: IBookedTrips) => !b.bookingId || !shownBookingIds.has(b.bookingId));
                    updateRecoilState("bookedTripsAtom", withShuttleFlag([...existing, ...newBookings]));
                } else {
                    updateRecoilState("bookedTripsAtom", withShuttleFlag(filteredTrips));
                }

                // Find Next Ticket (only for upcoming trips, only on the first
                // page). Pagination must not re-pick the next ticket from a
                // later page.
                let nearestRecord: IBookedTrips | any = null;
                if (!appendMode && activeTab === "1" && filteredTrips.length > 0) {
                    /**
                     * Filter booking which match below condition for Next Ticket display
                     * 1. Booking status must be "FULFILLED"
                     * Note: Removed shuttle filter to include all booking types
                     * for Next Ticket selection based on chronological order
                     */
                    const allFulfillmentStatusBooking = filteredTrips.filter(
                        (d) =>
                            d.status === "FULFILLED"
                    );

                    if (allFulfillmentStatusBooking.length > 0) {
                        const currentDateUTC4: any = toUTC4(new Date().toISOString());
                        // The REAL clock, not the appearance pivot: the appearance period keeps a
                        // departed train on the list, but "next ticket" is a promise about a train
                        // the passenger can still catch, and the quick-exchange gate below reads it.
                        const nowDubai = dayjs().tz("Asia/Dubai");
                        const sortedFulfilledUpcoming = allFulfillmentStatusBooking
                            .filter(
                                (booking) =>
                                    !!booking?.departureDateTime &&
                                    dayjs(booking.departureDateTime).tz("Asia/Dubai").isAfter(nowDubai),
                            )
                            .sort(
                                (a, b) =>
                                    new Date(a.departureDateTime).getTime() -
                                    new Date(b.departureDateTime).getTime(),
                            );
                        nearestRecord = sortedFulfilledUpcoming[0] || null;
                        if (nearestRecord) {
                            let isNextBookingPassengerSplit: boolean = false;

                            // If it's a round trip, split it and handle each leg
                            if (nearestRecord.bookingType === "roundTrip" && nearestRecord.inboundData) {
                                // Fetch booking data to properly filter passengers by offers
                                const originalBooking = bookedTrips.find(bt => bt.id === nearestRecord.bookingId);
                                let outboundPassengers = nearestRecord.passengers;
                                let inboundPassengers = nearestRecord.passengers;
                                // Which of the row's trips each half keeps: its train for the leg each
                                // serves (splitRowTripsByLeg, below), position only as the fallback.
                                let outboundTripIds: string[] = nearestRecord.tripIds?.slice(0, 1) || [];
                                let inboundTripIds: string[] = nearestRecord.tripIds?.slice(1) || [];

                                // Each leg's riders are whoever holds that train (legHolderIds, in
                                // bookingRowRiders), not the row's one passenger group; the row's own
                                // riders for the direction stand when there is nothing to read.
                                const passengersHolding = (booking: any, direction: string, directionIds: Set<string>) => {
                                    const holders = legHolderIds(booking, nearestRecord.tripIds, direction);
                                    if (!holders) {
                                        return nearestRecord.passengers.filter((p: any) => directionIds.has(p.id));
                                    }
                                    return (booking.passengers || [])
                                        .filter((passenger: IPassenger) => holders.has(passenger.id))
                                        .map(toCardPassenger);
                                };

                                if (originalBooking) {
                                    const bookingResponse: any = await getCachedOrFetchBooking(originalBooking.id);
                                    if (bookingResponse.status === 'ok' && bookingResponse.answer) {
                                        const { booking } = bookingResponse.answer;

                                        // Filter outbound passengers based on FULFILLED admissions in outbound offers.
                                        // Bucketed by the leg served, like its siblings below: an onward shuttle
                                        // is stamped OUT_BOUND whichever train it serves. Currently this only
                                        // narrows an allowlist applied to an already-narrower passenger set, so
                                        // the raw filter was harmless — converted so the block is consistent and
                                        // stays correct if that set ever widens.
                                        const outboundTrips = booking.trips.filter((t: any) =>
                                            shuttleServesDirection(t, booking.trips, TripTypeEnum.OUT_BOUND));
                                        const outboundOffers = booking.bookedOffers.filter((o: any) =>
                                            o.admissions?.some((admission: any) =>
                                                admission.status === "FULFILLED" &&
                                                outboundTrips.some((t: any) => admission.tripCoverage?.coveredTripId === t.id)
                                            )
                                        );
                                        const outboundPassengerIds = new Set<string>();
                                        outboundOffers.forEach((offer: any) => {
                                            offer.admissions?.forEach((admission: any) => {
                                                if (admission.status === "FULFILLED") {
                                                    admission.passengerIds?.forEach((pid: string) => outboundPassengerIds.add(pid));
                                                }
                                            });
                                        });
                                        outboundPassengers = passengersHolding(booking, TripTypeEnum.OUT_BOUND, outboundPassengerIds);

                                        // Filter inbound passengers based on FULFILLED admissions in inbound offers.
                                        // Leg-served bucketing, as above.
                                        const inboundTrips = booking.trips.filter((t: any) =>
                                            shuttleServesDirection(t, booking.trips, TripTypeEnum.IN_BOUND));
                                        const inboundOffers = booking.bookedOffers.filter((o: any) =>
                                            o.admissions?.some((admission: any) =>
                                                admission.status === "FULFILLED" &&
                                                inboundTrips.some((t: any) => admission.tripCoverage?.coveredTripId === t.id)
                                            )
                                        );
                                        const inboundPassengerIds = new Set<string>();
                                        inboundOffers.forEach((offer: any) => {
                                            offer.admissions?.forEach((admission: any) => {
                                                if (admission.status === "FULFILLED") {
                                                    admission.passengerIds?.forEach((pid: string) => inboundPassengerIds.add(pid));
                                                }
                                            });
                                        });
                                        inboundPassengers = passengersHolding(booking, TripTypeEnum.IN_BOUND, inboundPassengerIds);
                                        const byLeg = splitRowTripsByLeg(nearestRecord.tripIds, booking.trips);
                                        if (byLeg) {
                                            outboundTripIds = byLeg.outbound;
                                            inboundTripIds = byLeg.inbound;
                                        }
                                    }
                                }

                                const outboundTrip = {
                                    ...nearestRecord, // Base properties
                                    bookingType: "oneWayTrip",
                                    // Clear inbound-specific fields for this outbound leg representation
                                    inboundData: undefined,
                                    inboundOfferID: undefined,
                                    direction: TripTypeEnum.OUT_BOUND, // Explicitly set direction
                                    selectedPriceClass: nearestRecord?.selectedPriceClass || "",
                                    passengers: outboundPassengers,
                                    // Scope to the outbound half's trips (see outboundTripIds)
                                    tripIds: outboundTripIds,
                                    passengerData: {
                                        outbound: outboundPassengers.map((p: any) => p.id),
                                        inbound: [],
                                    },
                                };

                                const inboundTrip = {
                                    ...nearestRecord, // Base properties (includes original outbound's from/to, class etc.)
                                    bookingType: "oneWayTrip",

                                    // Overwrite with specific inbound leg data from nearestRecord.inboundData
                                    ...(nearestRecord.inboundData?.from && { from: nearestRecord.inboundData.from }),
                                    ...(nearestRecord.inboundData?.to && { to: nearestRecord.inboundData.to }),
                                    fromName: nearestRecord.inboundData.fromName,
                                    toName: nearestRecord.inboundData.toName,
                                    departureTime: nearestRecord.inboundData.departureTime,
                                    arrivalTime: nearestRecord.inboundData.arrivalTime,
                                    duration: nearestRecord.inboundData.duration,
                                    stops: nearestRecord.inboundData.stops,
                                    arrivalDateTime: nearestRecord.inboundData.arrivalDateTime,
                                    departureDateTime: nearestRecord.inboundData.departureDateTime,
                                    summary: nearestRecord.inboundData.summary,
                                    selectedPriceClass: nearestRecord?.inboundData?.selectedPriceClass || "",
                                    productName: nearestRecord?.inboundData?.productName || "",
                                    // isOutboundIsDirectTrain also inherited, might need re-evaluation based on inbound summary

                                    // Derive date strings for this leg
                                    startDate: nearestRecord.inboundData.departureDateTime
                                        ? new Date(
                                            nearestRecord.inboundData.departureDateTime,
                                        ).toLocaleDateString("en-US", {
                                            weekday: "short",
                                            day: "2-digit",
                                            month: "short",
                                        })
                                        : "",
                                    startDateFormat: nearestRecord.inboundData.departureDateTime
                                        ? getShortDateFromDateStringFormatted(
                                            nearestRecord.inboundData.departureDateTime,
                                        )
                                        : "",

                                    // This trip is now a one-way representation of the original inbound leg
                                    outBoundOfferID: nearestRecord.inboundOfferID, // Its "outbound" offer is the original inboundOfferID
                                    inboundData: undefined,         // It has no further inbound leg itself
                                    inboundOfferID: undefined,      // No inbound offer for this new one-way trip
                                    direction: TripTypeEnum.IN_BOUND,        // Explicitly set direction
                                    inboundConfirmedPrice: nearestRecord?.inboundData?.inboundConfirmedPrice,
                                    passengers: inboundPassengers,
                                    // Scope to the inbound half's trips (see inboundTripIds)
                                    tripIds: inboundTripIds,
                                    passengerData: {
                                        outbound: [],
                                        inbound: inboundPassengers.map((p: any) => p.id),
                                    },
                                };

                                // Set the outbound trip as the next ticket
                                updateRecoilState("bookedNextTripsAtom", withShuttleFlag([outboundTrip])[0]);

                                // Check if inbound leg has passengers with different exchange dates
                                const updatedUpcomingFilter = filteredTrips.filter(
                                    (trip) => trip.bookingId !== nearestRecord.bookingId,
                                );
                                const nowDubai = bookingsPastPivotDubai();

                                // Find the original booking data to check for passenger groups
                                const originalBookingTrip = bookedTrips.find(bt => bt.id === nearestRecord.bookingId);
                                if (originalBookingTrip) {
                                    const response: any = await getCachedOrFetchBooking(originalBookingTrip.id);
                                    if (response.status === 'ok' && response.answer) {
                                        const bookingResponse: IBookingResponse = response.answer;
                                        const { booking } = bookingResponse;

                                        // Find all inbound trips and their corresponding offers.
                                        // TRAIN trips only, for the symmetric reason given on cdtOutboundTrips
                                        // below: a shuttle sharing the leg's direction is not a passenger group,
                                        // and counting it here would split the inbound leg the same way.
                                        const inboundTrips = preferTrainTrips(booking.trips).filter((t: any) => t.direction === TripTypeEnum.IN_BOUND);
                                        const inboundTripsWithPassengers = new Map<string, any>();

                                        // Group passengers by their actual trip dates
                                        inboundTrips.forEach((trip: any) => {
                                            const offerForTrip = booking.bookedOffers.find((o: any) =>
                                                o.admissions?.some((admission: any) =>
                                                    admission.status === "FULFILLED" &&
                                                    admission.tripCoverage?.coveredTripId === trip.id
                                                )
                                            );
                                            if (offerForTrip && offerForTrip.admissions) {
                                                const fulfilledPassengers: string[] = [];
                                                const fulfillmentIds: string[] = [];

                                                offerForTrip.admissions.forEach((admission: any) => {
                                                    if (admission.status === "FULFILLED") {
                                                        fulfilledPassengers.push(...admission.passengerIds);
                                                        if (admission?.id) {
                                                            fulfillmentIds.push(admission.id);
                                                        }
                                                    }
                                                });

                                                if (fulfilledPassengers.length > 0) {
                                                    const tripKey = `${trip.startTime}_${trip.id}`;
                                                    inboundTripsWithPassengers.set(tripKey, {
                                                        trip: trip,
                                                        offer: offerForTrip,
                                                        passengers: fulfilledPassengers,
                                                        fulfillmentIds: fulfillmentIds
                                                    });
                                                }
                                            }
                                        });

                                        // If there are multiple inbound trips with passengers, create separate bookings
                                        if (inboundTripsWithPassengers.size > 1) {
                                            isNextBookingPassengerSplit = true;
                                            inboundTripsWithPassengers.forEach((tripData, tripKey) => {
                                                const { trip: inboundTripData, offer: inboundOfferData, passengers: passengerIds, fulfillmentIds } = tripData;
                                                if (!inboundTripData?.startTime) {
                                                    return;
                                                }
                                                const inboundTripDubai = dayjs(inboundTripData.startTime).tz("Asia/Dubai");
                                                if (!inboundTripDubai.isAfter(nowDubai)) {
                                                    return;
                                                }

                                                const groupPassengers = booking.passengers
                                                    .filter((p: IPassenger) => passengerIds.includes(p.id))
                                                    .map(toCardPassenger);

                                                // Use FULFILLED admission data (not admissions[0] which may be EXCHANGED)
                                                const inboundFulfilledAdmission = inboundOfferData?.admissions?.find((a: any) => a.status === "FULFILLED");
                                                const separateInboundTrip = {
                                                    ...nearestRecord,
                                                    bookingType: "oneWayTrip",
                                                    fromName: stopManager.getStopNameById(inboundTripData.origin.stopPlaceRef),
                                                    toName: stopManager.getStopNameById(inboundTripData.destination.stopPlaceRef),
                                                    from: inboundTripData.origin.stopPlaceRef,
                                                    to: inboundTripData.destination.stopPlaceRef,
                                                    departureTime: convertTimeToArabic(inboundTripData.startTime),
                                                    arrivalTime: convertTimeToArabic(inboundTripData.endTime),
                                                    duration: convertISO8601ToDuration(inboundTripData.duration),
                                                    stops: inboundTripData.transfers > 0 ? Array(inboundTripData.transfers).fill("Stop") : [],
                                                    arrivalDateTime: inboundTripData.endTime,
                                                    departureDateTime: inboundTripData.startTime,
                                                    class: inboundFulfilledAdmission?.summary?.split(" ")[0] || "",
                                                    summary: inboundFulfilledAdmission?.summary || "",
                                                    selectedPriceClass: getTrainClassTypeBaseOnProduct(inboundOfferData?.products || []) || "",
                                                    productName: getProductNameFromOffer(inboundOfferData),
                                                    startDate: inboundTripData.startTime
                                                        ? new Date(inboundTripData.startTime).toLocaleDateString("en-US", {
                                                            weekday: "short",
                                                            day: "2-digit",
                                                            month: "short",
                                                        })
                                                        : "",
                                                    startDateFormat: inboundTripData.startTime
                                                        ? getShortDateFromDateStringFormatted(inboundTripData.startTime)
                                                        : "",
                                                    outBoundOfferID: inboundOfferData?.offerId,
                                                    inboundData: undefined,
                                                    inboundOfferID: undefined,
                                                    inboundConfirmedPrice: calculatePrice(inboundFulfilledAdmission?.price?.amount || 0),
                                                    passengers: groupPassengers,
                                                    passengerData: { outbound: [], inbound: passengerIds },
                                                    fulfillmentIds: fulfillmentIds,
                                                    id: `${nearestRecord.id}_${inboundTripData.id}`,
                                                    bookingId: nearestRecord.bookingId,
                                                    tripIds: [inboundTripData.id],
                                                    // As the single-inbound row above sets it. Spread from the
                                                    // outbound row, this one had none, so a fold could not tell
                                                    // it was a return leg and drew the moved group's round trip
                                                    // as one-way.
                                                    direction: TripTypeEnum.IN_BOUND,
                                                };

                                                updatedUpcomingFilter.push(separateInboundTrip);
                                            });
                                        } else {
                                            // Only one inbound trip with passengers, add single inbound trip as before
                                            if (inboundTrip?.departureDateTime) {
                                                const inboundTripDubai = dayjs(inboundTrip.departureDateTime).tz("Asia/Dubai");
                                                if (inboundTripDubai.isAfter(nowDubai)) {
                                                    updatedUpcomingFilter.push(inboundTrip);
                                                }
                                            }
                                        }

                                        // CDT partial-passenger: also recreate outbound trips for other passenger groups
                                        // The nearestRecord's outbound is already the "next ticket", but other CDT
                                        // passenger groups had their outbound removed by the bookingId filter above.
                                        //
                                        // TRAIN trips only. This block's whole premise is "more than one outbound
                                        // trip means more than one passenger group", and an onward SHUTTLE breaks
                                        // it: the backend tags shuttle trips OUT_BOUND — including the one that
                                        // runs after the inbound train lands — so an ordinary single-passenger
                                        // round trip with a shuttle at each end presents THREE outbound trips.
                                        // The `size > 1` guard below then fired on booking UR5SK2 and minted a card
                                        // per shuttle, so one booking rendered as four rows on /bookings (next
                                        // ticket + inbound + two shuttles), every one stamped with the same
                                        // reference. A shuttle is not a passenger group.
                                        const cdtOutboundTrips = preferTrainTrips(booking.trips).filter((t: any) => t.direction === TripTypeEnum.OUT_BOUND);
                                        const outboundTripsWithPassengers = new Map<string, any>();

                                        cdtOutboundTrips.forEach((trip: any) => {
                                            const offerForTrip = booking.bookedOffers.find((o: any) =>
                                                o.admissions?.some((admission: any) =>
                                                    admission.status === "FULFILLED" &&
                                                    admission.tripCoverage?.coveredTripId === trip.id
                                                )
                                            );
                                            if (offerForTrip && offerForTrip.admissions) {
                                                const fulfilledPassengers: string[] = [];
                                                const fulfillmentIds: string[] = [];

                                                offerForTrip.admissions.forEach((admission: any) => {
                                                    if (admission.status === "FULFILLED") {
                                                        fulfilledPassengers.push(...admission.passengerIds);
                                                        if (admission?.id) {
                                                            fulfillmentIds.push(admission.id);
                                                        }
                                                    }
                                                });

                                                if (fulfilledPassengers.length > 0) {
                                                    const tripKey = `${trip.startTime}_${trip.id}`;
                                                    outboundTripsWithPassengers.set(tripKey, {
                                                        trip: trip,
                                                        offer: offerForTrip,
                                                        passengers: fulfilledPassengers,
                                                        fulfillmentIds: fulfillmentIds
                                                    });
                                                }
                                            }
                                        });

                                        // Only add outbound cards for trips NOT already shown as "next ticket"
                                        if (outboundTripsWithPassengers.size > 1) {
                                            outboundTripsWithPassengers.forEach((tripData) => {
                                                const { trip: outboundTripData, offer: outboundOfferData, passengers: passengerIds, fulfillmentIds } = tripData;
                                                if (!outboundTripData?.startTime) {
                                                    return;
                                                }
                                                const outboundTripDubai = dayjs(outboundTripData.startTime).tz("Asia/Dubai");
                                                if (!outboundTripDubai.isAfter(nowDubai)) {
                                                    return;
                                                }

                                                // Skip the trip that's already the "next ticket"
                                                if (nearestRecord.tripIds?.includes(outboundTripData.id)) return;

                                                const groupPassengers = booking.passengers
                                                    .filter((p: IPassenger) => passengerIds.includes(p.id))
                                                    .map(toCardPassenger);

                                                // Use FULFILLED admission data (not admissions[0] which may be EXCHANGED)
                                                const outboundFulfilledAdmission = outboundOfferData?.admissions?.find((a: any) => a.status === "FULFILLED");
                                                const separateOutboundTrip = {
                                                    ...nearestRecord,
                                                    bookingType: "oneWayTrip",
                                                    fromName: stopManager.getStopNameById(outboundTripData.origin.stopPlaceRef),
                                                    toName: stopManager.getStopNameById(outboundTripData.destination.stopPlaceRef),
                                                    from: outboundTripData.origin.stopPlaceRef,
                                                    to: outboundTripData.destination.stopPlaceRef,
                                                    departureTime: convertTimeToArabic(outboundTripData.startTime),
                                                    arrivalTime: convertTimeToArabic(outboundTripData.endTime),
                                                    duration: convertISO8601ToDuration(outboundTripData.duration),
                                                    stops: outboundTripData.transfers > 0 ? Array(outboundTripData.transfers).fill("Stop") : [],
                                                    arrivalDateTime: outboundTripData.endTime,
                                                    departureDateTime: outboundTripData.startTime,
                                                    class: outboundFulfilledAdmission?.summary?.split(" ")[0] || "",
                                                    summary: outboundFulfilledAdmission?.summary || "",
                                                    selectedPriceClass: getTrainClassTypeBaseOnProduct(outboundOfferData?.products || []) || "",
                                                    productName: getProductNameFromOffer(outboundOfferData),
                                                    startDate: outboundTripData.startTime
                                                        ? new Date(outboundTripData.startTime).toLocaleDateString("en-US", {
                                                            weekday: "short",
                                                            day: "2-digit",
                                                            month: "short",
                                                        })
                                                        : "",
                                                    startDateFormat: outboundTripData.startTime
                                                        ? getShortDateFromDateStringFormatted(outboundTripData.startTime)
                                                        : "",
                                                    outBoundOfferID: outboundOfferData?.offerId,
                                                    inboundData: undefined,
                                                    inboundOfferID: undefined,
                                                    direction: TripTypeEnum.OUT_BOUND,
                                                    passengers: groupPassengers,
                                                    passengerData: { outbound: passengerIds, inbound: [] },
                                                    fulfillmentIds: fulfillmentIds,
                                                    id: `${nearestRecord.id}_${outboundTripData.id}`,
                                                    bookingId: nearestRecord.bookingId,
                                                    tripIds: [outboundTripData.id],
                                                };

                                                updatedUpcomingFilter.push(separateOutboundTrip);
                                            });
                                        }
                                    } else {
                                        // Failed to get booking data, add single inbound trip as before
                                        if (inboundTrip?.departureDateTime) {
                                            const inboundTripDubai = dayjs(inboundTrip.departureDateTime).tz("Asia/Dubai");
                                            if (inboundTripDubai.isAfter(nowDubai)) {
                                                updatedUpcomingFilter.push(inboundTrip);
                                            }
                                        }
                                    }
                                } else {
                                    // Original booking not found, add single inbound trip as before
                                    if (inboundTrip?.departureDateTime) {
                                        const inboundTripDubai = dayjs(inboundTrip.departureDateTime).tz("Asia/Dubai");
                                        if (inboundTripDubai.isAfter(nowDubai)) {
                                            updatedUpcomingFilter.push(inboundTrip);
                                        }
                                    }
                                }
                                // Sort the updated upcoming filter
                                const sortedUpcomingFilter = updatedUpcomingFilter.sort(
                                    (a, b) => {
                                        // First sort by status (FULFILLED first)
                                        if (a.status === "FULFILLED" && b.status !== "FULFILLED")
                                            return -1;
                                        if (a.status !== "FULFILLED" && b.status === "FULFILLED")
                                            return 1;

                                        // Then sort by departure date
                                        return (
                                            new Date(b.departureDateTime).getTime() -
                                            new Date(a.departureDateTime).getTime()
                                        );
                                    },
                                );

                                // Re-folded on SAME RIDERS only. Every other booking here was folded
                                // above (the `filteredTrips` collapse); the next ticket's own rows are
                                // rebuilt one per LEG, and after a partial change-date a plain fold
                                // joined the unchanged inbound (whole party) to the moved passenger's
                                // new outbound through that one shared rider, drawing a round trip
                                // nobody holds. One group's two legs still fold into its card.
                                updateRecoilState(
                                    "bookedTripsAtom",
                                    withShuttleFlag(collapseBookingRows(sortedUpcomingFilter, { sameRidersOnly: true }))
                                );
                            } else {
                                // Set the next booking
                                nearestRecord.bookingId = nearestRecord.id;
                                updateRecoilState("bookedNextTripsAtom", withShuttleFlag([nearestRecord])[0]);
                            }

                            // List + next-trip atoms are now populated. Unblock the UI
                            // before the optional exchange-offers prefetch runs — that
                            // call returns ~2 MB and ~2.4 s and is only used to render
                            // "previous/next train" chips, so it must not gate first paint.
                            _eventBus.emit({ eventName: "BOOKING_DATA_LOADED" });

                            let departureDateISO: any = toUTC4(nearestRecord.departureDateTime);
                            // SIGNED, not Math.abs: the rule below is "the next
                            // ticket is more than an hour AWAY", and an absolute
                            // difference answers it just as happily for a train
                            // that left 90 minutes ago. That was unreachable while
                            // a departed train could never be the next ticket —
                            // the appearance period makes that a filter three
                            // hundred lines up rather than a property of this
                            // check, so the check now stands on its own.
                            let timeDifference: number = departureDateISO - currentDateUTC4;

                            // Convert timeDifference to hours
                            let timeDifferenceInHours: number = timeDifference / 3600000;

                            /**
                             * Filter booking which match below condition for quick exchange
                             * 1. Quick exchange show only when Next Ticket have 1 hour gap between current time to next ticket departure time
                             * 2. Next ticket must be a train booking ticket not shuttle booking
                             * 3. Quick exchange is not available for retrieve booking and guest retrieve booking
                             * 4. Quick exchange is not available if passengers are split between legs
                             */

                            const isPassengerSplitBooking = (() => {
                                if (isNextBookingPassengerSplit) {
                                    return true;
                                }

                                const passengerData = nearestRecord?.passengerData;
                                if (passengerData) {
                                    const outboundIds: string[] = passengerData.outbound || [];
                                    const inboundIds: string[] = passengerData.inbound || [];

                                    if (outboundIds.length > 0 && inboundIds.length > 0) {
                                        const sortAndJoin = (arr: string[]) =>
                                            Array.from(new Set(arr)).sort().join("|");

                                        if (sortAndJoin(outboundIds) !== sortAndJoin(inboundIds)) {
                                            return true;
                                        }
                                    }
                                }

                                if (nearestRecord.bookingType === "oneWayTrip" && filteredTrips && filteredTrips.length > 0) {
                                    const sameBookingTrips = filteredTrips.filter(
                                        (t) => t.bookingId === nearestRecord.bookingId
                                    );

                                    if (sameBookingTrips.length > 1) {
                                        const getPassengerKey = (trip: IBookedTrips) =>
                                            Array.from(new Set((trip.passengers || []).map((p: any) => p.id)))
                                                .sort()
                                                .join("|");

                                        const baseKey = getPassengerKey(nearestRecord);

                                        for (const trip of sameBookingTrips) {
                                            if (trip === nearestRecord) continue;
                                            if (getPassengerKey(trip) !== baseKey) {
                                                return true;
                                            }
                                        }
                                    }
                                }

                                return false;
                            })();

                            if (
                                timeDifferenceInHours > 1 &&
                                !isPassengerSplitBooking &&
                                !nearestRecord.summary.toLowerCase().includes("shuttle") &&
                                !nearestRecord.isRetrieveBooking &&
                                nearestRecord.noModificationOnBooking
                            ) {
                                // Update next and previous train atoms
                                updateRecoilState("nextAndPreviousTrainAtom", {
                                    previousTrain: null,
                                    nextTrain: null,
                                });

                                // Fire-and-forget: this call returns ~2 MB / ~2.4 s and
                                // only feeds the "previous/next train" chips on the next
                                // ticket card. Awaiting it blocks the bookings page render
                                // and the loader. Let it run in the background and update
                                // nextAndPreviousTrainAtom when it lands.
                                try {
                                    getExchangeOfferByBooking.setParams({
                                        nextBooking: nearestRecord,
                                        fromUpcoming: true,
                                        cachedBookingResponse: bookingResponseCache.get(nearestRecord.bookingId)
                                    });
                                    void getExchangeOfferByBooking.execute().catch(() => {
                                        updateRecoilState("nextAndPreviousTrainAtom", {
                                            previousTrain: null,
                                            nextTrain: null,
                                        });
                                    });
                                } catch (error) {
                                    updateRecoilState("nextAndPreviousTrainAtom", {
                                        previousTrain: null,
                                        nextTrain: null,
                                    });
                                }
                            } else {
                                updateRecoilState("nextAndPreviousTrainAtom", {
                                    previousTrain: null,
                                    nextTrain: null,
                                });
                            }
                        } else {
                            updateRecoilState("bookedNextTripsAtom", null);
                            updateRecoilState("nextAndPreviousTrainAtom", {
                                previousTrain: null,
                                nextTrain: null,
                            });
                            _eventBus.emit({ eventName: "BOOKING_DATA_LOADED" });
                        }
                    } else {
                        updateRecoilState("bookedNextTripsAtom", null);
                        updateRecoilState("nextAndPreviousTrainAtom", {
                            previousTrain: null,
                            nextTrain: null,
                        });
                        _eventBus.emit({ eventName: "BOOKING_DATA_LOADED" });
                    }
                } else if (!appendMode) {
                    /**
                    * @recoil showRetrieveBookingAtom
                    * This is managing retrieve booking show and hide.
                    *
                    * Skip on append (Load more) — an empty subsequent page must
                    * not flip the retrieve-booking UI on. hasMore=false handles
                    * that case at the pagination layer.
                    */
                    if (activeTab == "1") {
                        updateRecoilState('showRetrieveBookingAtom', true);
                    }
                    updateRecoilState('bookedNextTripsAtom', null);
                    updateRecoilState("nextAndPreviousTrainAtom", {
                        previousTrain: null,
                        nextTrain: null,
                    });
                    _eventBus.emit({ eventName: "BOOKING_DATA_LOADED" });
                } else {
                    // Append-mode with zero new fulfilled rows: just unblock the loader.
                    _eventBus.emit({ eventName: "BOOKING_DATA_LOADED" });
                }
            }

            if (exchangeOngoingPairs.length > 0) {
                autoCancelOngoingExchangesAction.setParams({
                    pairs: exchangeOngoingPairs,
                });
                void autoCancelOngoingExchangesAction.execute();
            }
        } catch (error: any) {
            console.error("Error fetching booking detail:", error);
        }
    }
}

export const getBookingToRightDataAction = new GetBookingToRightDataAction();
